import dayjs from "dayjs";
import { ApiConfig } from "./ApiConfig";
import { ApiCallDelete, ApiCallGet, ApiCallPost, ApiCallPut } from "./invoke";
import { buildPayslipSettingPayload } from "../utils/payslipSettings";

const serializeTaxRulePayload = (payload = {}) => {
  const {
    taxLabelId,
    country,
    taxName,
    type,
    payType,
    paidBy,
    description,
    startDate,
    endDate,
    forAllEmp,
    employeeValue,
    employerValue,
    maxCap,
  } = payload;

  const body = {
    taxLabelId,
    country,
    taxName,
    type,
    payType,
    paidBy,
    description,
    startDate,
    endDate,
    forAllEmp: typeof forAllEmp === "boolean" ? forAllEmp : false,
  };

  if (paidBy === "Employee" || paidBy === "Both") {
    body.employeeValue = employeeValue;
  }

  if (paidBy === "Employer" || paidBy === "Both") {
    body.employerValue = employerValue;
  }

  if (maxCap !== undefined && maxCap !== null && maxCap !== "") {
    body.maxCap = maxCap;
  }

  return body;
};
// Optional bulk-report filters; omitted when empty so the backend keeps company-wide behaviour.
const bulkReportFilters = (filters) => {
  const out = {};
  if (filters?.department && filters.department !== "all") out.department = filters.department;
  if (filters?.rateType) out.rateType = filters.rateType;
  return out;
};

const AuthService = {
  //post reuqests
  login: async (data) => {
    const { baseUrl, login } = ApiConfig;

    const url = baseUrl + login;
    const params = {
      email: data?.email?.value,
      password: data?.password?.value,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    return ApiCallPost(url, params, headers);
  },

  signup: async (data) => {
    const { baseUrl, signUp } = ApiConfig;

    const url = baseUrl + signUp;
    const params = {
      firstName: data?.firstName?.value,
      lastName: data?.lastName?.value,
      email: data?.email?.value,
      password: data?.password?.value,
      password_confirmation: data?.confirmPassword?.value,
      phone: data?.phone?.value,
      countryCode: data?.countryCode?.value,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    return ApiCallPost(url, params, headers);
  },

  // Verify email using the token embedded in the emailed link. No auth header:
  // the token in the body is what authenticates the request. On success the
  // backend returns a fresh JWT ({ isEmailVerified: true, token }).
  verifyEmail: async (token) => {
    const { baseApi, verifyEmail } = ApiConfig;

    const url = baseApi + verifyEmail;
    const params = { token };

    const headers = {
      "Content-Type": "application/json",
    };

    return ApiCallPost(url, params, headers);
  },

  // Resend the verification email. Rate-limited server-side; always returns a
  // generic success. Works with or without an auth token (email in body).
  resendVerificationEmail: async (email) => {
    const { baseApi, resendVerificationEmail } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + resendVerificationEmail;
    const params = { email };

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return ApiCallPost(url, params, headers);
  },

  // Trigger an SMS OTP for phone verification (auth required, no body).
  sendPhoneOtp: async () => {
    const { baseApi, sendPhoneOtp } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + sendPhoneOtp;
    const params = {};

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  // Verify the SMS OTP (auth required). Success returns { isPhoneVerified: true }.
  verifyPhoneOtp: async (otp) => {
    const { baseApi, verifyPhoneOtp } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + verifyPhoneOtp;
    const params = { otp };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  // ---- Notifications -------------------------------------------------------
  // Unread badge count. Polled from the bell.
  getNotificationUnreadCount: () => {
    const { baseApi, notificationUnreadCount } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + notificationUnreadCount;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  // List for the drawer. `status` = "unread" | "read" (omit for all).
  getNotifications: (page, status, limit) => {
    const { baseApi, notification } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (limit) params.append("limit", limit);
    if (status && status !== "all") params.append("status", status);
    const url = `${baseApi}${notification}${params.toString() ? `/?${params.toString()}` : ""}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  // Fetching a single notification marks it read as a side effect — only call
  // on an explicit item click, never for list previews.
  getNotificationById: (id) => {
    const { baseApi, notification } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${notification}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  markNotificationRead: (id) => {
    const { baseApi, notification } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${notification}/${id}/read`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, {}, headers);
  },

  markAllNotificationsRead: () => {
    const { baseApi, notificationMarkAllRead } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + notificationMarkAllRead;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, {}, headers);
  },

  // ---- SuperAdmin broadcast console ---------------------------------------
  // POST /notification/admin body:
  //   { title, body, priority, expiresAt, isPublished,
  //     targetAudience: { type: "all"|"company"|"role"|"user",
  //                       companyId?, role?, recipientId? } }
  //   `type` is NOT sent — server hardcodes it to "manual".
  createAdminNotification: (payload) => {
    const { baseApi, notificationAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + notificationAdmin;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },

  // GET /notification/admin — list of broadcasts with recipientCount/readCount.
  getAdminNotifications: (page) => {
    const { baseApi, notificationAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    const url = `${baseApi}${notificationAdmin}${params.toString() ? `/?${params.toString()}` : ""}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  updateAdminNotification: (id, payload) => {
    const { baseApi, notificationAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${notificationAdmin}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },

  deleteAdminNotification: (id) => {
    const { baseApi, notificationAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${notificationAdmin}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, {}, headers);
  },

  download: async (selectedType, fromDate, toDate, email, employee, data) => {
    const { baseApi, download } = ApiConfig;

    const url = baseApi + download;
    const token = localStorage.getItem("accessToken");

    const params = {
      type: selectedType,
      employee: employee,
      email: email,
      fromDate: fromDate,
      toDate: toDate,
      data: data,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  getPayrollStats: async () => {
    const { baseApi, payrollStats } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + payrollStats;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, params, headers);
  },
  getAllStats: async () => {
    const { baseApi, allStats } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + allStats;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, params, headers);
  },
  companyInfo: async (data, cCode, mobile) => {
    const { baseApi, company } = ApiConfig;

    const url = baseApi + company;
    const token = localStorage.getItem("accessToken");

    const params = {
      companyName: data?.companyName?.value,
      owner: data?.owner?.value,
      email: data?.email?.value,
      state: data?.states?.value,
      city: data?.city?.value,
      country: data?.country?.value,
      zipCode: data?.zipCode?.value,
      industry: data?.industry?.value,
      cellNumber: mobile,
      cellNumberCCode: cCode || "+27",
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  createPayroll: async (
    fromDate,
    toDate,
    frequency,
    payrollName,
    employees,
    paymentMethod,
    department
  ) => {
    const { baseApi, createPayroll } = ApiConfig;

    const url = baseApi + createPayroll;
    const token = localStorage.getItem("accessToken");
    // const company = localStorage.getItem("id");

    const params = {
      employees: employees,
      name: payrollName,
      paymentMethod: paymentMethod,
      frequency: frequency,
      start: fromDate,
      department: department,

      end: toDate,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  getindustry: () => {
    const { baseApi, industry } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + industry;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAllTaxLabel: () => {
    const { baseApi, getAllTaxLabel } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getAllTaxLabel;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getTaxPeriod: (taxCountry) => {
    const { baseApi, taxPeriod } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + taxPeriod;
    const params = taxCountry ? { country: taxCountry } : {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSelectedCountries: () => {
    const { baseApi, selectedCountries } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + selectedCountries;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getProfileView: async () => {
    const { baseApi, profileView } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + profileView;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const result = await ApiCallGet(url, params, headers);
    if (result?.success && result?.data) {
      if (typeof result.data.is_subscription !== "undefined") {
        localStorage.setItem("is_subscription", result.data.is_subscription);
      }
      if (typeof result.data.billing_info !== "undefined") {
        localStorage.setItem("billing_info", result.data.billing_info);
      }
      if (typeof result.data.billingInfo !== "undefined") {
        localStorage.setItem(
          "billingInfo",
          JSON.stringify(result.data.billingInfo)
        );
      }
      localStorage.setItem("isExpired", result?.data?.isExpired)
      localStorage.setItem("isTrialExpired", result?.data?.isTrialExpired)
      window.dispatchEvent(new Event("subscription_updated"));
    }
    return result;
  },
  handleNewEmployee: async ({ data }) => {
    const { baseUrl, signup } = ApiConfig;
    const companyId = localStorage.getItem("id");
    // return;
    const url = baseUrl + signup;
    const params = {
      companyId: companyId,
      data: data,
    };

    const headers = {
      "Content-Type": "application/json",
    };
    return ApiCallPost(url, params, headers);
  },

  handleApplyRequest: async (
    leaveType,
    leaveFrom,
    leaveTo,
    days,
    tz,
    id,
    reason
  ) => {
    const { baseApi, applyLeaveRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const company = localStorage.getItem("id");
    const role = localStorage.getItem("role");
    // return;
    const url = baseApi + applyLeaveRequest + id;
    const adminUrl = baseApi + applyLeaveRequest + id;
    const params = {
      leaveType: leaveType,
      leaveFrom: leaveFrom,
      leaveTo: leaveTo,
      timezone: tz,
      noOfDays: days,
      employeeId: id,
      company: company,
      reason: reason,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(role === "Admin" ? adminUrl : url, params, headers);
  },
  handleUpdateLeaveRequest: async (
    leaveId,
    leaveType,
    leaveFrom,
    leaveTo,
    days,
    tz,
    reason
  ) => {
    const { baseApi, updateLeaveRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateLeaveRequest + '/' + leaveId;
    const params = {
      leaveType: leaveType,
      leaveFrom: leaveFrom,
      leaveTo: leaveTo,
      timezone: tz,
      noOfDays: days,
      reason: reason,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleApplyCommission: async (
    employee,
    commissionName,
    bonus,
    fromDate,
    toDate,
    fixed,
    description
  ) => {
    const { baseApi, applyCommission } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    // const company = localStorage.getItem("id");
    const role = localStorage.getItem("role");
    // return;
    const url = baseApi + applyCommission;
    const adminUrl = baseApi + applyCommission;
    const params = {
      start: fromDate,
      end: toDate,
      employees: employee,
      rate: bonus,
      rateType: fixed,
      name: commissionName,
      desc: description,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(role === "Admin" ? adminUrl : url, params, headers);
  },

  handleApplyTravelAllowances: async (payload) => {
    const { baseApi, applayTravelAllowances } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + applayTravelAllowances;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },

  handleProject: async (projectName) => {
    const { baseApi, project } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    // return;
    const url = baseApi + project;
    const params = {
      name: projectName,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleCountrySelection: async (countries) => {
    const { baseApi, countrySelection } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + countrySelection;

    const params = {
      countries: countries,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleExport: async (email, fromDate, toDate, type) => {
    const { baseApi, attendanceExport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    // const email = localStorage.getItem("email");
    // return;
    const url = baseApi + attendanceExport;
    const params = {
      email: email,
      fromDate: fromDate,
      toDate: toDate,
      format: type,
    };

    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        type === "pdf"
          ? "application/pdf"
          : type === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };

    return ApiCallPost(url, params, headers, "blob");
  },
  handleImport: async (file) => {
    const { baseApi, attendanceImport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + attendanceImport;
    const params = {
      file,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleAddRecruitment: async (
    position,
    company,
    quantity,
    department,
    workplace,
    displaySalary,
    startingSalaryFrom,
    startingSalaryTo,
    reason,
    desc,
    fromDate,
    toDate,
    workingFrom
  ) => {
    const { baseApi, addRecruitment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    // const companyId = localStorage.getItem("id");
    // return;
    const url = baseApi + addRecruitment;
    const params = {
      // company: companyId,
      // company:data?.company?.value,
      position: position,
      fromDate: fromDate,
      toDate: toDate,
      quantity: quantity,
      department: department,
      workplace: workplace,
      salaryEnd: startingSalaryTo,
      salaryStart: startingSalaryFrom,
      reason: reason,
      desc: desc,
      type: workingFrom,
      isShowSalary: displaySalary,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleImgUrl: (data) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    const company = localStorage.getItem("id");
    data.forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });
    formData.append("path", `${company}/profile`);

    const { baseApi, imagesUpload } = ApiConfig;
    const url = baseApi + imagesUpload;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, formData, headers);
  },

  handleSingleImgUrl: (data) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    const company = localStorage.getItem("id");

    formData.append("images", data);
    formData.append("path", `${company}/profile`);

    const { baseApi, imagesUpload } = ApiConfig;
    const url = baseApi + imagesUpload;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, formData, headers);
  },

  handleAttendanceImport: (data) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    // const company = localStorage.getItem("id");

    formData.append("file", data);
    // formData.append("path", `${company}/profile`);

    const { baseApi, attendanceImport } = ApiConfig;
    const url = baseApi + attendanceImport;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, formData, headers);
  },

  addNewEmploye: async (
    data,
    imgUrl,
    mobile,
    cCode,
    document,
    emergency,
    emergencyCode,
    workplace,
    workplaceCode
  ) => {
    const token = localStorage.getItem("accessToken");
    const { baseApi, addNewEmployee } = ApiConfig;
    const url = baseApi + addNewEmployee;
    const params = {
      personalInfo: {
        firstName: data?.firstName?.value,
        surName: data?.lastName?.value,
        nickName: data?.nickName?.value,
        gender: data?.gender?.value,
        mobile: mobile,
        cCode: cCode || "+27",
        password: data?.password?.value,
        password_confirmation: data?.confirmPassword?.value,
        jobPosition: [data?.jobPosition?.value],
        department: data?.department?.value,
        operator: data?.operator?.value,
        addressLine1: data?.address1?.value,
        addressLine2: data?.address2?.value,
        city: data?.city?.value,
        zipCode: data?.zipCode?.value,
        country: data?.country?.value,
        email: data?.email?.value,
        dob: dayjs(data?.dob?.value).format("YYYY-MM-DD"),
        education: data?.education?.value,
        image: imgUrl[0],
      },
      // empId: data?.empNumber?.value,

      otherInformation: {
        nationality: data?.nationality?.value,
        religion: data?.religion?.value,
        martial: data?.marital?.value,
        passportNo: data?.passportNum?.value,
        emergencyContact: emergency,
        workplace: workplace,
        emergencyCCode: emergencyCode || "+27",
        workplaceCCode: workplaceCode || "+27",
      },
      bankInfo: {
        bank: data?.bankName?.value,
        account: data?.accountNum?.value,
        bankCode: data?.ifscCode?.value,
        accountHolder: data?.accountHolder?.value,
        taxCode: data?.personalTaxCode?.value,
        acType: data?.accountType?.value,
        // issuedDate: dayjs(data?.issuedDate?.value).format("YYYY-MM-DD"),
      },
      salaryInfo: {
        salary: Number(data?.salary?.value),
        hRate: data?.hourlyRate?.value,
        restraintTrade: data?.restraintTrade.value,
        // annualBonus: data?.annualBonus.value,
        payfreq: data?.payfreq.value,
        paymentType: data?.paymentType?.value,
        rateRembTravel: data?.rateRembTravel?.value || 0,
      },
      experience: {
        joiningDate: dayjs(data?.joiningDate?.value).format("YYYY-MM-DD"),
        exp: data?.experience?.value,
      },
      isActive: data?.isActive?.value ?? true,

      additionalInformation: {
        isprofile: data?.profileVisibility?.value
          ? data?.profileVisibility?.value
          : false,
        istasknoti: data?.taskNoti?.value ? data?.taskNoti?.value : false,
        isTracked: data?.isTracked?.value ? data?.isTracked?.value : false,
        isOverTime: data?.isOvertime?.value || false,
        overTimeRate: data?.rate?.value || "0",
        isExtraTime: data?.isExtraTime?.value || false,
        extraTimeRate: data?.extraTimeRate?.value || "0",
        isDoubleOverTime: data?.isDoubleOverTime?.value || false,
        doubleOverTimeRate: data?.doubleOverTimeRate?.value || "0",

        empType: data?.empType?.value,

        contractor: {
          start: data?.start?.value,
          end: data?.end?.value,
        },

        isPreAttendance: data?.isPreAttendance?.value || false,
      },
      socialMedia: {
        skype: data?.urlSkype?.value,
        instagram: data?.urlInsta?.value,
        linkedIn: data?.urlLinkedin?.value,
        facebook: data?.urlFb?.value,
      },
      taxInfo: {
        idNumber: data?.idNumber?.value,
        taxRefNo: data?.taxRefNo?.value,
        medicalAidNo: data?.medicalAidNo?.value,
      },
      projects: Array.isArray(data?.projects?.value)
        ? data.projects.value.map((proj) => proj.name)
        : [],
    };

    const validDocs = Array.isArray(document) ? document.filter(d => d.url || d.name) : [];
    if (validDocs.length > 0) {
      params.docs = validDocs;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  postTimesheet: async (project, date, desc, tz, employeeId) => {
    const { baseApi, timesheet } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("id");
    const role = localStorage.getItem("role");
    const url = baseApi + timesheet;
    const adminUrl = baseApi + timesheet;
    const params = {
      employees: employeeId,
      companyId: id,
      work: project,
      date: date,
      timezone: tz,
      desc: desc,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(role === "Admin" ? adminUrl : url, params, headers);
  },
  AddNewItem: async (payload) => {
    const { baseApi, deleteItem } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteItem;
    const params = { ...payload };
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  handleAddBank: (data, mobile, cCode) => {
    const { baseApi, getOneBank } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getOneBank;
    const params = {
      name: data?.bankName?.value,
      branch: data?.branch?.value,
      accountHolder: data?.accountHolder?.value,
      accountNumber: data?.accountNumber?.value,
      phone: mobile,
      cCode: cCode,
      swiftCode: data?.swiftCode?.value,
      initialBalance: Number(data?.initialBalance?.value),
      bankingURL: data?.internetBanking?.value,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleAddCompanyBankAccount: (data) => {
    const { baseApi, addCompanyBankAccount } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addCompanyBankAccount;
    const params = {
      accountName: data?.accountName,
      accountCode: data?.accountCode,
      accountNumber: data?.accountNumber,
      bankName: data?.bankName,
      ifsc: data?.ifsc,
      description: data?.description,
      currency: data?.currency,
      isPrimary: data?.isPrimary,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  handleGetAllCompanyBankAccount: (text, sort, page) => {
    const { baseApi, getCompanyBankAccounts } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);

    const url = `${baseApi}${getCompanyBankAccounts}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleGetAllTransactionData: (invoiceId) => {
    const { baseApi, getAllTransactionData } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}${getAllTransactionData}/${invoiceId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleGetCashAccount: () => {
    const { baseApi, getCashAccount } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};

    const url = `${baseApi}${getCashAccount}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, params, headers);
  },

  handleAddAccountTransaction: (data) => {
    const { baseApi, addAccountTransaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addAccountTransaction;
    const params = {
      account: data?.account,
      date: data?.date,
      description: data?.description,
      status: data?.status,
      paidVia: data?.paidVia,
      deposit: data?.deposit || "",
      withdrawal: data?.withdrawal || "",
      reference: data?.reference,
      tds: data?.tds,
      transactionType: data?.transactionType,
      labelType: data?.labelType,
      labelName: data?.labelName,
      accountType: data?.accountType
    };
    if (data?.vendor) {
      params.vendor = data?.vendor;
    }
    if (data?.customer) {
      params.customer = data?.customer;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  handleAddAccountReconciliation: (data) => {
    const { baseApi, addAccountReconciliation } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addAccountReconciliation;
    const params = {
      account: data?.account,
      startDate: data?.startDate,
      endDate: data?.endDate,
      reconciledTransactionIds: data?.reconciledTransactionIds,
      status: data?.status,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  handleGetAccountReconciliation: (accountID, text, sort, page, status) => {
    const { baseApi, getAccountReconciliations } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (status) params.append("status", status);

    const url = `${baseApi}${getAccountReconciliations}/${accountID}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleFinalizeAccountReconciliation: (reconciliationId, data) => {
    const { baseApi, finalizeAccountReconciliation } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {
      category: data?.category,
      expenseAccount: data?.expenseAccount,
      reportingTags: data?.reportingTags,
      amount: data?.amount,
      date: data?.date,
      description: data?.description,
      reference: data?.reference,
    };

    const url = `${baseApi}${finalizeAccountReconciliation}/${reconciliationId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  handleUnodAccountReconciliation: (reconciliationId) => {
    const { baseApi, undoAccountReconciliation } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};

    const url = `${baseApi}${undoAccountReconciliation}/${reconciliationId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  handleGetAccountReconciliationDetails: (
    reconciliationId,
    text,
    sort,
    page
  ) => {
    const { baseApi, getAccountReconciliationDetails } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);

    const url = `${baseApi}${getAccountReconciliationDetails}/${reconciliationId}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleDeleteAccountReconciliation: (reconciliationId) => {
    const { baseApi, deleteAccountReconiliation } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};

    const url = `${baseApi}${deleteAccountReconiliation}/${reconciliationId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallDelete(url, params, headers);
  },

  handleAddStatementTransaction: (formData) => {
    const { baseApi, addStatementTransaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addStatementTransaction;

    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, formData, headers);
  },

  handleGetAllAccountTransaction: (accountID, text, sort, page, status) => {
    const { baseApi, getAccountTransactions } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (status) params.append("status", status);

    const url = `${baseApi}${getAccountTransactions}/${accountID}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleUpdteUncategorisedTransaction: (transactionId, data) => {
    const { baseApi, updateUncategorisedTransaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {
      category: data?.category,
      expenseAccount: data?.expenseAccount,
      reportingTags: data?.reportingTags,
      amount: data?.amount,
      date: data?.date,
      description: data?.description,
      reference: data?.reference,
    };
    if (data?.vendor) {
      params.vendor = data?.vendor;
    } else if (data?.customer) {
      params.customer = data?.customer;
    }

    const url = `${baseApi}${updateUncategorisedTransaction}/${transactionId}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  handleGetAccountTransactionsByDateRange: (accountId, startDate, endDate) => {
    const { baseApi, getAccountTransactionsByDateRange } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getAccountTransactionsByDateRange}/${accountId}?startDate=${startDate}&endDate=${endDate}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  handleGetAllCreditNotes: () => {
    const { baseApi, getAllCreditNotes } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getAllCreditNotes}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleGetCreditNote: (creditNoteId) => {
    const { baseApi, getCreditNote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getCreditNote}/${creditNoteId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleViewCreditNote: (creditNoteId) => {
    const { baseApi, viewCreditNote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${viewCreditNote}/${creditNoteId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleDownloadCreditNote: (creditNoteId) => {
    const { baseApi, downloadCreditNote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${downloadCreditNote}/${creditNoteId}`;

    const headers = {
      Accept: "application/pdf",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers, "blob");
  },

  handleDeleteCreditNote: (creditNoteId) => {
    const { baseApi, deleteCreditNote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${deleteCreditNote}/${creditNoteId}`;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    return ApiCallDelete(url, params, headers);
  },

  getEmployeeList: (text, page, sort, getFilterVaue, type, extraFilters = {}) => {
    const { baseApi, getEmployeeList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("id");
    const params = new URLSearchParams();

    // Only add "search" if "text" is not empty
    if (text) params.append("search", text);
    if (page) params.append("page", page);
    if (sort) params.append("sort", sort);
    if (getFilterVaue) params.append("filter", getFilterVaue);
    if (type) params.append("type", type);
    // Optional list filters: department, role, startDate, endDate (YYYY-MM-DD)
    ["department", "role", "startDate", "endDate"].forEach((key) => {
      if (extraFilters?.[key]) params.append(key, extraFilters[key]);
    });

    const url = `${baseApi}${getEmployeeList}${id}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  getEmployeeListForLeave: (text, page, sort, getFilterVaue, getSection = 'leave') => {
    const { baseApi, getEmployeeList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("id");
    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (page) params.append("page", page);
    if (sort) params.append("sort", sort);
    if (getFilterVaue) params.append("filter", getFilterVaue);
    if (getSection) params.append("section", getSection);

    const url = `${baseApi}${getEmployeeList}${id}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  deductionDownload: () => {
    const { baseApi, deductionDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deductionDownload;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },

  deductionTransactionDownload: (id) => {
    const { baseApi, dedutionTransactionDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + dedutionTransactionDownload + `/${id}`;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },

  loanDownload: () => {
    const { baseApi, loanDownLoad } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + loanDownLoad;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },

  loanTransactionDownload: (id) => {
    const { baseApi, loanTransactionDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + loanTransactionDownload + `/${id}`;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },

  leaveDownload: () => {
    const { baseApi, leaveDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + leaveDownload;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },

  getCountry: (country) => {
    const { baseApi, getCountry } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    // const id = localStorage.getItem("id");
    const params = new URLSearchParams();


    const url = `${baseApi}${getCountry}?country=${country}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getYearTaxTable: (id, country) => {
    const { baseApi, yearTaxTable } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const encodedCountry = country ? encodeURIComponent(country) : "";
    const url = `${baseApi}${yearTaxTable}?id=${id}&country=${encodedCountry}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSchedule: (companyToken) => {
    const { baseApi, getSchedule } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = `${baseApi}${getSchedule}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getTimesheetList: (text, sort, page) => {
    const { baseApi, getTimesheetList } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const id = localStorage.getItem("id");
    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);
    const url = `${baseApi}${getTimesheetList}${id}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    // const url = baseApi + getTimesheetList+id+"?search="+text;
    // const urlMain = baseApi + getTimesheetList+id;
    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmployeeTimesheet: (from, to, employeeID, page) => {
    const { baseApi, getEmployeeTimesheetList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (from) params.append("fromDate", dayjs(from).format("YYYY-MM-DD"));
    if (to) params.append("toDate", dayjs(to).format("YYYY-MM-DD"));
    if (page) params.append("page", page);
    const url = `${baseApi}${getEmployeeTimesheetList}${employeeID}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getProjectList: () => {
    const { baseApi, getProjectList } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getProjectList;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmpLeave: (id) => {
    const { baseApi, getEmpLeave } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getEmpLeave + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSalaryInfo: (id) => {
    const { baseApi, updateSalary } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + updateSalary + "info/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmployeeDetails: (id) => {
    const { baseApi, getEmployeeDetails } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getEmployeeDetails + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAttendanceList: (text, filter, fromDate, toDate, status, sort, page) => {
    const { baseApi, getAttendance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("id");
    const params = new URLSearchParams();

    if (fromDate)
      params.append("fromDate", dayjs(fromDate).format("YYYY-MM-DD"));
    if (toDate) params.append("toDate", dayjs(toDate).format("YYYY-MM-DD"));
    if (status) params.append("status", status);
    if (filter) params.append("filter", filter);
    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getAttendance}${id}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  getItemsList: (text, sort, page) => {
    const { baseApi, getItemList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getItemList}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  getOneItem: (id) => {
    const { baseApi, deleteItem } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + deleteItem + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getProfile: () => {
    const { baseApi, profile } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + profile;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getProjectsAdmin: (text) => {
    const { baseApi, projectAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (text) params.append("search", text);

    const url = `${baseApi}${projectAdmin}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getCheckIn: () => {
    const { baseApi, IsCheckin } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + IsCheckin;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getLeaveType: () => {
    const { baseApi, getLeaveType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getLeaveType;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getLeaveManagementList: (text, sort, page) => {
    const { baseApi, getleaveManagement } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("id");
    // const url = baseApi + getleaveManagement;

    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getleaveManagement}${id}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  getLeaveBalanceList: (leaveType, page, limit = 10) => {
    const { baseApi, geLeaveBalance } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const queryParts = [];
    if (leaveType) queryParts.push(`leaveType=${encodeURIComponent(leaveType)}`);
    if (page) queryParts.push(`page=${encodeURIComponent(page)}`);
    if (limit) queryParts.push(`limit=${encodeURIComponent(limit)}`);

    const queryString = queryParts.length ? `/?${queryParts.join("&")}` : "";
    const url = `${baseApi}${geLeaveBalance}${queryString}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  getCommissionList: (text, sort, page) => {
    const { baseApi, getCommission } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getCommission}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getTravelAllowances: (text, sort, page) => {
    const { baseApi, getTravelAllowance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getTravelAllowance}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getRecruitmentData: (text, page) => {
    const { baseApi, getRecruitment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("employeeId");
    const company = localStorage.getItem("id");
    const role = localStorage.getItem("role");
    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getRecruitment}${role === "Admin" ? company : id}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getRecruitmentDetail: (id) => {
    const { baseApi, getSingleRecruitment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = baseApi + getSingleRecruitment + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSalarySlip: (id) => {
    const { baseApi, updateSalary } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const responseType = "blob";
    const url = baseApi + updateSalary + "slip/" + id;
    const headers = {
      "Content-Type": "application/pdf",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers, responseType);
  },
  getSalaryList: (text, sort, page, start, end) => {
    const { baseApi, getSalaryView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);
    if (start) params.append("start", start);
    if (end) params.append("end", end);

    const url = `${baseApi}${getSalaryView}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  getSalaryData: () => {
    const { baseApi, updateSalary } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    // if (text) params.append("search", text);
    // if (sort) params.append("sort", sort);
    const url = `${baseApi}${updateSalary}total`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSalaryStats: () => {
    const { baseApi, salaryStats } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = `${baseApi}${salaryStats}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPayrollList: (text, filter, sort, page) => {
    const { baseApi, getPayrollList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const id = localStorage.getItem("employeeId");
    const company = localStorage.getItem("id");
    const role = localStorage.getItem("role");
    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (filter) params.append("filter", filter);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getPayrollList}${role === "Admin" ? company : id}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPayroll: (id) => {
    const { baseApi, getPayroll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getPayroll + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPayrollPayslip: (id) => {
    const { baseApi, payrollPayslip } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    // const url = `${baseApi}${payrollPayslip}?payrollId=${payrollId}&employeeId=${id}`;
    const url = baseApi + payrollPayslip + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPayrollPayslipData: (id, payrollId) => {
    const { baseApi, getPayrollPayslip } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getPayrollPayslip}?payrollId=${payrollId}&employeeId=${id}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getSalaryInfoData: (id) => {
    const { baseApi, salaryInfo } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${salaryInfo}${id}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getPensionList: (id, pId) => {
    const { baseApi, getPension } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getPension + id + "/" + pId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAllowanceList: (id, pId) => {
    const { baseApi, getAllowance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getAllowance + id + "/" + pId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getLevieList: (id, pId) => {
    const { baseApi, getLevie } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getLevie + id + "/" + pId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getFringeList: (id, pId) => {
    const { baseApi, getFringe } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getFringe + id + "/" + pId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getLoanPayslip: (id, pId) => {
    const { baseApi, getLoanPayslip } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getLoanPayslip + id + "/" + pId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAllowanceType: () => {
    const { baseApi, allowanceType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + allowanceType;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getFringeType: () => {
    const { baseApi, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + fringeType;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getLeviesType: () => {
    const { baseApi, leviesType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + leviesType;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getDeductionType: () => {
    const { baseApi, deductionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deductionType;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmpAllowance: (id) => {
    const { baseApi, empAllowances } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + empAllowances + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmpFringeBenefits: (id) => {
    const { baseApi, empFringeBenefits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + empFringeBenefits + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmpLevies: (id) => {
    const { baseApi, empLevie } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + empLevie + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmpPension: (id) => {
    const { baseApi, empPensionsingle } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    // if (id) params.append(id)
    // const url = `${baseApi}${empPension}${params.toString() ? `/?${params.toString()}` : ""}`;
    const url = baseApi + empPensionsingle + "/" + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getContributions: (id) => {
    const { baseApi, getContributions } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getContributions + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmpMedical: (id) => {
    const { baseApi, getMedical } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getMedical + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getDeductionPayslip: (id) => {
    const { baseApi, payslipTransaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + payslipTransaction + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getLeavePayslip: (id, from, to) => {
    const { baseApi, getLeavePayslip } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}${getLeavePayslip}/${id}/?fromDate=${from}&toDate=${to}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleEmpAllowance: (data, id) => {
    const { baseApi, empAllowances } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + empAllowances;
    const params = {
      ...data,
      employee: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleEmpLevie: (data, id) => {
    const { baseApi, handleLevie } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLevie;
    const params = {
      ...data,
      employee: id,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleEmpFringeBenefit: (data, id) => {
    const { baseApi, empFringeBenefits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + empFringeBenefits;
    const params = {
      ...data,
      employee: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  removeEmpAllowance: (employeeId, id) => {
    const { baseApi, removeAllowance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + removeAllowance;
    const params = {
      employee: employeeId,
      id: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  removeEmpContribution: (employeeId, id) => {
    const { baseApi, removeContribution } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + removeContribution;
    const params = {
      employee: employeeId,
      id: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  removeEmpFringeBenefit: (employeeId, id) => {
    const { baseApi, removeFringeBenefit } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + removeFringeBenefit;
    const params = {
      employee: employeeId,
      id: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  removeEmpLevy: (employeeId, id) => {
    const { baseApi, removeLevy } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + removeLevy;
    const params = {
      employee: employeeId,
      id: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleEmpPension: (id, data) => {
    const { baseApi, handleEmpPension } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleEmpPension;
    function removeIsDeletedKey(dataArray) {
      return dataArray.map(({ isDeleted, ...rest }) => {
        return rest;
      });
    }

    const updatedArray = removeIsDeletedKey(data);
    const params = {
      employee: id,
      contributions: updatedArray,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleContributions: (id, data) => {
    const { baseApi, handleContributions } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleContributions;
    const params = {
      ...data,
      employee: id,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleEmpMedical: (data, id) => {
    const { baseApi, handleEmpMedical } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleEmpMedical;
    const params = {
      employee: id,
      employeeCont: data[0]?.employeeCont,
      employerCont: data[0]?.employerCont,
      payment: data[0]?.payment,

      // ...data[0]
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handlePension: (id, type, amount, payrollId, employeeId) => {
    const { baseApi, getPension } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getPension + payrollId;
    const params = {
      employee: employeeId,
      salaryId: id,
      type: type,
      amount: amount,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleTaxyear: (date) => {
    const { baseApi, getPayslipTaxyear } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getPayslipTaxyear;
    const params = date;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleLeave: (id, leaveFrom, leaveTo, leaveType, payrollId, employeeId) => {
    const { baseApi, handleLeave } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLeave + payrollId;
    const params = {
      employee: employeeId,
      salaryId: id,
      leaveFrom: leaveFrom,
      leaveTo: leaveTo,
      leaveType: leaveType,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleLoanPayslip: (id, payrollId, employeeId, payload, loanId) => {
    const { baseApi, handleLoanPayslip } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLoanPayslip + payrollId;
    const params = {
      loanTrans: loanId,
      employee: employeeId,
      salaryId: id,
      repayAmt: payload?.[0]?.repayAmt,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleDeductionPayslip: (id, payrollId, employeeId, deduction) => {
    const { baseApi, getDeductionTranasaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getDeductionTranasaction + "/" + payrollId;

    const formattedDeductions = deduction.map(d => ({
      type: d.type,
      rateValue: d.rateValue,
    }));

    const params = {
      employee: employeeId,
      salaryId: id,
      deductions: formattedDeductions,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleAllowance: (id, type, amount, payrollId, employeeId) => {
    const { baseApi, getAllowance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getAllowance + payrollId;
    const params = {
      employee: employeeId,
      salaryId: id,
      type: type,
      amount: amount,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleLevie: (id, payrollId, employeeId, rateValue) => {
    const { baseApi, getLevie } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getLevie + payrollId;
    const params = {
      employee: employeeId,
      salaryId: id,
      rateValue: rateValue[0].rateValue,
      levyTrans: rateValue[0].levyTrans,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleFringe: (id, type, amount, payrollId, employeeId) => {
    const { baseApi, getFringe } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getFringe + payrollId;
    const params = {
      employee: employeeId,
      salaryId: id,
      type: type,
      amount: amount,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleSupAllowance: (name, tax, id) => {
    const { baseUrl, allowanceType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${allowanceType}/?id=${id}`;
    const params = {
      name: name,
      isTaxable: tax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleTaxTable: (
    min,
    max,
    fixedAmount,
    ratePercentage,
    country,
    datefrom,
    dateTo,
    textTabelId,
    // yearId,
    // frequency
  ) => {
    const { baseApi, taxTable } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url =
      textTabelId === undefined || textTabelId === null
        ? baseApi + taxTable
        : baseApi + taxTable + "/?id=" + textTabelId;
    const preparedMin =
      min === null || min === undefined ? undefined : Number(min);
    const preparedMax =
      max === null || max === undefined ? undefined : Number(max);
    const preparedFixed =
      fixedAmount === null || fixedAmount === undefined
        ? undefined
        : Number(fixedAmount);
    const preparedRate =
      ratePercentage === null || ratePercentage === undefined
        ? undefined
        : Number(ratePercentage);

    const taxSlab = {};
    if (preparedMin !== undefined && !Number.isNaN(preparedMin)) {
      taxSlab.minIncome = preparedMin;
    }
    if (preparedMax !== undefined && !Number.isNaN(preparedMax)) {
      taxSlab.maxIncome = preparedMax;
    }
    if (preparedFixed !== undefined && !Number.isNaN(preparedFixed)) {
      taxSlab.fixedAmount = preparedFixed;
    }
    if (preparedRate !== undefined && !Number.isNaN(preparedRate)) {
      taxSlab.ratePercentage = preparedRate;
    }

    const params = {
      taxSlabs: [taxSlab],
    };

    if (country) {
      params.country = country;
    }
    if (datefrom) {
      params.startYear = datefrom;
    }
    if (dateTo) {
      params.endYear = dateTo;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleEditTaxTable: (
    id,
    min,
    max,
    fixedAmount,
    ratePercentage,
    textTabelId,
  ) => {
    const { baseApi, taxTable } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + taxTable + "/" + textTabelId;
    const params = {
      taxSlabId: id,
    };
    const preparedMin =
      min === null || min === undefined ? undefined : Number(min);
    const preparedMax =
      max === null || max === undefined ? undefined : Number(max);
    const preparedFixed =
      fixedAmount === null || fixedAmount === undefined
        ? undefined
        : Number(fixedAmount);
    const preparedRate =
      ratePercentage === null || ratePercentage === undefined
        ? undefined
        : Number(ratePercentage);

    if (preparedMin !== undefined && !Number.isNaN(preparedMin)) {
      params.minIncome = preparedMin;
    }
    if (preparedMax !== undefined && !Number.isNaN(preparedMax)) {
      params.maxIncome = preparedMax;
    }
    if (preparedFixed !== undefined && !Number.isNaN(preparedFixed)) {
      params.fixedAmount = preparedFixed;
    }
    if (preparedRate !== undefined && !Number.isNaN(preparedRate)) {
      params.ratePercentage = preparedRate;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleSupFringe: (name, tax, id) => {
    const { baseUrl, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${fringeType}/?id=${id}`;
    const params = {
      name: name,
      isTaxable: tax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleSupPension: (name, tax, id) => {
    const { baseUrl, pensionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${pensionType}/?id=${id}`;
    const params = {
      name: name,
      isTaxable: "false",
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleSupTax: (start, end, id) => {
    const { baseApi, handleTaxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleTaxType}/?id=${id}`;
    const params = {
      start: start,
      end: end,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleTransfer: (email) => {
    const { baseApi, handleTransfer } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleTransfer;
    const params = {
      email: email,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleSupLeave: (payload, id) => {
    const { baseApi, handleLeaveSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleLeaveSetting}/${id}`;
    const params = {
      type: payload.type,
      method: payload.method,
      rate: payload.rate,
      yearlyCap: payload.yearlyCap,
      carryToNextFY: payload.carryToNextFY,
      country: payload.country,
      start: payload.start,
      end: payload.end,

    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handlePayslip: async (id, user, paid, travelValue) => {
    const { baseApi, updateSalary } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateSalary + id;

    // const url = `${baseApi}${updateSalary}?payrollId=${payrollId}&employeeId=${id}`;
    const params = {
      isPaid: paid || false,
      commission: Number(user?.commission?.value) || 0,
      // taxAdjust: Number(user?.netTax?.value),
      uifAdjust: Number(user?.uif?.value) || 0,
      isShowEmpty: user?.isShow?.value || false,
      kilometer: Number(travelValue) || 0,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleProcessAll: async (start, end) => {
    const { baseApi, updateProcess } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateProcess;

    const params = {
      start: start,
      end: end,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handlePrintAll: async (start, end) => {
    const { baseApi, printALL } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();
    if (start) params.append("start", start);
    if (end) params.append("end", end);
    const url = `${baseApi}${printALL}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, {}, headers, "blob");
  },
  handleEmailAll: async (start, end) => {
    const { baseApi, emailAll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (start) params.append("start", start);
    if (end) params.append("end", end);
    const url = `${baseApi}${emailAll}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, {}, headers);
  },

  getAllBanks: (text, sort, page) => {
    const { baseApi, getAllBanks } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getAllBanks}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getInvoice: (text, sort, page) => {
    const { baseApi, getInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getInvoice}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getRecurringInvoiceTemplates: (text, sort, page) => {
    const { baseApi, getAllRecurringInvoiceTemplates } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getAllRecurringInvoiceTemplates}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  deleteRecurringInvoiceTemplate: (recurringTemplateId) => {
    const { baseApi, deleteRecurringInvoiceTemplate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};

    const url = `${baseApi}${deleteRecurringInvoiceTemplate}/${recurringTemplateId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getRecurringInvoiceTemplateDetail: (recurringTemplateId) => {
    const { baseApi, getRecurringInvoiceTemplateDetail } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};

    const url = `${baseApi}${getRecurringInvoiceTemplateDetail}/${recurringTemplateId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  updateRecurringInvoiceTemplate: (recurringTemplateId, data) => {
    const { baseApi, updateRecurringInvoiceTemplate } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = {
      customer: data.customer._id,
      billingAddress: data.billingAddress,
      shippingAddress: data.shippingAddress,
      paymentTerms: data.paymentTerms,
      dailyPenaltyAmount: data.dailyPenaltyAmount,
      otherInformation: data.otherInformation,
      productInformation: data.productInformation,
      discountPercentage: data.discountPercentage,
      discountValue: data.discountValue,
      taxPercentage: data.taxPercentage,
      taxValue: data.taxValue,
      total: data.total,
      grandTotal: data.grandTotal,
      profileName: data.profileName,
      repeatEvery: data.repeatEvery,
      startOn: data.startOn,
      endOn: data.endOn,
    };

    const url = `${baseApi}${updateRecurringInvoiceTemplate}/${recurringTemplateId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  getPurchase: (text, sort, page) => {
    const { baseApi, getPurchase } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getPurchase}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getBill: (id) => {
    const { baseApi, getAllBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getAllBill + "/" + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getOneBill: (id) => {
    const { baseApi, handleBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + handleBill + "/" + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getBillView: (id) => {
    const { baseApi, getBillView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getBillView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleAddNewBill: (id, payload) => {
    const { baseApi, markReceive } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + markReceive + "/" + id + "/convertToBill";
    const params = payload;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  deleteRefundBillHistory: (id, transactionId) => {
    const { baseApi, handleBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleBill}/${id}/payment/${transactionId}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  getInvoiceView: (id) => {
    const { baseApi, getInvoiceView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getInvoiceView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSales: (text, sort, page) => {
    const { baseApi, getSales } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getSales}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  editProfile: async (data, _imgUrl, user) => {
    const token = localStorage.getItem("accessToken");
    const role = localStorage.getItem("role");
    const { baseApi, profileView } = ApiConfig;
    const url = baseApi + profileView;
    const inputPhone = data?.mobile?.value;
    const normalizedInputPhone =
      inputPhone === null || inputPhone === undefined
        ? ""
        : String(inputPhone).trim();
    const fallbackPhone =
      user?.personalInfo?.phone || user?.personalInfo?.mobile || "";
    const phoneValue = normalizedInputPhone || fallbackPhone;
    const userPersonalInfo = user?.personalInfo || {};
    const {
      password: _ignoredPassword,
      password_confirmation: _ignoredPasswordConfirmation,
      confirmPassword: _ignoredConfirmPassword,
      ...safeUserPersonalInfo
    } = userPersonalInfo;
    const personalInfoBase =
      role === "Employee" ? safeUserPersonalInfo : userPersonalInfo;
    const userBillingInfo = user?.billingInfo || {};
    const userBankInfo = user?.bankInfo || {};
    const hasExplicitImageUpdate = _imgUrl !== null && _imgUrl !== undefined;
    const uploadedImage = Array.isArray(_imgUrl) ? _imgUrl?.[0] : _imgUrl;
    const inputSurname = data?.lastName?.value;
    const savedSurname = user?.personalInfo?.surName || "";
    const isSavedSurnameEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(savedSurname);
    const surNameValue = inputSurname || (isSavedSurnameEmail ? "" : savedSurname);

    const builtParams = {
      personalInfo: {
        ...personalInfoBase,
        firstName: data?.firstName?.value ?? user?.personalInfo?.firstName,
        surName: surNameValue,
        nickName: data?.nickName?.value ?? user?.personalInfo?.nickName,
        gender: data?.gender?.value ?? user?.personalInfo?.gender,
        countryCode: data?.countryCode?.value ?? user?.personalInfo?.countryCode ?? user?.personalInfo?.cCode,
        phone: phoneValue,
        jobPosition: data?.jobPosition?.value
          ? Array.isArray(data?.jobPosition?.value)
            ? data?.jobPosition?.value
            : [data?.jobPosition?.value]
          : user?.personalInfo?.jobPosition,
        department: data?.department?.value ?? user?.personalInfo?.department,
        addressLine1: data?.address1?.value ?? user?.personalInfo?.addressLine1,
        addressLine2: data?.address2?.value ?? user?.personalInfo?.addressLine2,
        city: data?.city?.value ?? user?.personalInfo?.city,
        state: data?.states?.value ?? user?.personalInfo?.state,
        zipCode: data?.zipCode?.value ?? user?.personalInfo?.zipCode,
        country: data?.country?.value ?? user?.personalInfo?.country,
        email: data?.email?.value ?? user?.personalInfo?.email,
        dob: data?.dob?.value ?? user?.personalInfo?.dob,
        education: data?.education?.value ?? user?.personalInfo?.education,
        image: hasExplicitImageUpdate ? (uploadedImage || "") : (personalInfoBase?.image || ""),
        ...(data?.password?.value && {
          password: data?.password?.value,
          password_confirmation: data?.confirmPassword?.value,
        }),
      },
      billingInfo: {
        ...userBillingInfo,
        address1: data?.address1?.value ?? user?.billingInfo?.address1,
        address2: data?.address2?.value ?? user?.billingInfo?.address2,
        city: data?.city?.value ?? user?.billingInfo?.city,
        state: data?.states?.value ?? user?.billingInfo?.state,
        zipcode: data?.zipCode?.value ?? user?.billingInfo?.zipcode,
        country: data?.country?.value ?? user?.billingInfo?.country,
      },
      bankInfo: {
        ...userBankInfo,
        bank: data?.bankName?.value ?? user?.bankInfo?.bank,
        account: data?.accountNum?.value ?? user?.bankInfo?.account,
        bankCode: data?.ifscCode?.value ?? (user?.bankInfo?.bankCode || user?.bankInfo?.ifsc),
        accountHolder: data?.accountHolder?.value ?? user?.bankInfo?.accountHolder,
        taxCode: data?.personalTaxCode?.value ?? (user?.bankInfo?.taxCode || user?.bankInfo?.vat),
        acType: data?.accountType?.value ?? user?.bankInfo?.acType,
        issuedDate: data?.issuedDate?.value ?? user?.bankInfo?.issuedDate,
      },
    };

    const diffObjects = (built, original) => {
      const diff = {};
      for (const key in built) {
        const newVal = built[key];
        const oldVal = original?.[key];

        if (Array.isArray(newVal) && Array.isArray(oldVal)) {
          if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
            diff[key] = newVal;
          }
        } else if (Array.isArray(newVal) && (!oldVal || !Array.isArray(oldVal))) {
          diff[key] = newVal;
        } else if (newVal != oldVal) {
          const isEffectivelyUnchanged = (!newVal || newVal === "") && (!oldVal || oldVal === "");
          if (!isEffectivelyUnchanged) {
            diff[key] = newVal;
          }
        }
      }
      return diff;
    };

    const params = {};
    const effectivePersonalInfo = {
      ...userPersonalInfo,
      phone: userPersonalInfo?.phone || userPersonalInfo?.mobile,
      countryCode: userPersonalInfo?.countryCode || userPersonalInfo?.cCode,
      addressLine1: userPersonalInfo?.addressLine1 || userBillingInfo?.address1,
      addressLine2: userPersonalInfo?.addressLine2 || userBillingInfo?.address2,
      city: userPersonalInfo?.city || userBillingInfo?.city,
      state: userPersonalInfo?.state || userPersonalInfo?.states || userBillingInfo?.state,
      zipCode: userPersonalInfo?.zipCode || userBillingInfo?.zipcode,
      country: userPersonalInfo?.country || userBillingInfo?.country,
    };
    const personalDiff = diffObjects(builtParams.personalInfo, effectivePersonalInfo);
    if (Object.keys(personalDiff).length > 0) params.personalInfo = personalDiff;

    const bankDiff = diffObjects(builtParams.bankInfo, userBankInfo);
    if (Object.keys(bankDiff).length > 0) params.bankInfo = bankDiff;

    const billingDiff = diffObjects(builtParams.billingInfo, userBillingInfo);
    if (Object.keys(billingDiff).length > 0) params.billingInfo = billingDiff;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  handleTimesheet: async (project, date, desc, id) => {
    const { baseApi, timesheetUpdate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + timesheetUpdate;
    const params = {
      work: project,
      date: date,
      desc: desc,
      timesheetId: id,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleUpdateDeduction: async (
    id,
    employeeId,
    type,
    amount,
    typeD,
    salary,
    desc,
    fromDate,
    toDate,
    paymentMethod
  ) => {
    const { baseApi, getDeduction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getDeduction + "/" + id;
    const params = {
      // id: id,
      employee: employeeId,
      deductions: [
        {
          type: type,
          rateValue: amount,
          payType: typeD,
          deductType: salary === "" ? "Salary" : salary,
          desc: desc,
          start: fromDate,
          end: toDate,
          paymentMethod: paymentMethod,
        },
      ],
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleUpdateLoan: async (
    id,
    employeeId,
    date,
    repayment,
    loan,
    repaymentAmount,
    numberRepayments,
    des,
    remark,
    paymentMethod
  ) => {
    const { baseApi, handleLoan } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLoan + "/" + id;
    const params = {
      // id: id,
      employee: employeeId,
      taxYear: date,
      repayStart: repayment,
      desc: des,
      loanAmt: loan,
      repayAmt: repaymentAmount,
      numRepay: numberRepayments,
      remark: remark,
      paymentMethod: paymentMethod,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleEditBank: (data, id, mobile, cCode) => {
    const { baseApi, getOneBank } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getOneBank + "/" + id;
    const params = {
      name: data?.bankName?.value,
      branch: data?.branch?.value,
      accountHolder: data?.accountHolder?.value,
      accountNumber: data?.accountNumber?.value,
      phone: Number(mobile) || "",
      cCode: Number(cCode) || "",
      swiftCode: data?.swiftCode?.value,
      initialBalance: Number(data?.initialBalance?.value),
      bankingURL: data?.internetBanking?.value,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleEditItem: (id, payload, files) => {
    const { baseApi, deleteItem } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteItem + "/" + id;
    const params = { ...payload };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleTimesheetRequest: (id, request, isPaid) => {
    const { baseApi, timesheetRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + timesheetRequest;
    const params = {
      timesheetId: id,
      status: request,
      isPaid: isPaid,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handlePermission: (data, id) => {
    const { baseApi, setPermission } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + setPermission + id;
    const params = {
      employee: data?.employee,
      attendance: {
        isView: data?.attendance?.isView,
        scope: data?.attendance?.scope || "view",
        isEdit: data?.attendance?.isEdit,
        isEditOwn: data?.attendance?.isEditOwn,
        isEditDepartment: data?.attendance?.isEditDepartment || false,
        departments: data?.attendance?.departments || [],
      },
      leave: data?.leave,
      payroll: data?.payroll,
      payslip: data?.payslip,
      permission: data?.permission,
      recruitment: data?.recruitment,
      salary: data?.salary,
      timesheet: data?.timesheet,
      loan: data?.loan,
      deduction: data?.deduction,
      account: data?.account,
      commission: data?.commission,
      travelAllowance: data?.travelAllowance,
      reports: data?.reports,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  getEmployeePermission: (employeeId) => {
    const { baseApi, getEmployeePermission } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getEmployeePermission + employeeId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleUpdatePayroll: (payroll, payrollId) => {
    const { baseApi, updatePayroll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updatePayroll + payrollId;
    const params = {
      name: payroll,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleAddAddition: (expenseName, additionAmount, id) => {
    const { baseApi, updateAddition } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateAddition + id;
    const params = {
      expense: expenseName,
      amount: Number(additionAmount),
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handlededuction: (expenseName, additionAmount, id) => {
    const { baseApi, updateDeduction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateDeduction + id;
    const params = {
      name: expenseName,
      amount: Number(additionAmount),
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleSalaryRequest: (request, id) => {
    const { baseApi, updateSalary } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateSalary + request + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleAddOvertime: (project, overtime, rate, hours, salaryId) => {
    const { baseApi, updateOvertime } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateOvertime + salaryId;
    const params = {
      project: project,
      overtime: overtime,
      rate: rate,
      hours: hours,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleAddPay: (refNum, salaryId) => {
    const { baseApi, updatePay } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updatePay + salaryId;
    const params = {
      transaction: refNum,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  updateProject: (projectName, status, projectId) => {
    const { baseApi, project } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + project;
    const params = { projectId: projectId };

    if (projectName) {
      params.name = projectName;
    }

    if (status) {
      params.status = status;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleLeaveRequest: (id, status, isPaid) => {
    const { baseApi, handleLeaveRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLeaveRequest + id;
    const params = {
      status: status,
      isPaid: isPaid,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleCloseRecruitment: (id) => {
    const { baseApi, closeRecruitment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + closeRecruitment + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  updateRecruitment: (
    position,
    company,
    quantity,
    department,
    workplace,
    displaySalary,
    startingSalaryFrom,
    startingSalaryTo,
    reason,
    desc,
    fromDate,
    toDate,
    workingFrom,
    id
  ) => {
    const { baseApi, updateRecruitment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateRecruitment + id;
    const params = {
      position: position,
      fromDate: fromDate,
      toDate: toDate,
      quantity: quantity,
      department: department,
      workplace: workplace,
      salaryEnd: startingSalaryTo,
      salaryStart: startingSalaryFrom,
      reason: reason,
      desc: desc,
      type: workingFrom,
      isShowSalary: displaySalary,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handlePayrollEmployee: (id, status, employeeId) => {
    const { baseApi, updatePayroll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updatePayroll + status;
    const params = {
      payrollId: id,
      employeeId: employeeId,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handlePayrollStatus: (id, status) => {
    const { baseApi, updatePayroll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updatePayroll + id;
    const params = {
      status: status,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleIncreament: (employees, rate, date, fixed, increment) => {
    const { baseApi, updateIncreament } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateIncreament;
    const params = {
      employees: employees,
      rate: rate,
      date: date,
      rateType: fixed,
      isSalaryAdjustment: increment,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  // DELETE Requests
  deleteEmployee: (id, value) => {
    const { baseApi, deleteEmployee } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteEmployee + id;

    const params = {
      action: value,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteEmpPension: (id) => {
    const { baseApi, handleEmpPension } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleEmpPension + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteCompany: (id) => {
    const { baseApi, deleteCompany } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteCompany + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteContributions: (id) => {
    const { baseApi, handleContributions } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleContributions + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteAttendance: (id) => {
    const { baseApi, deleteAttendance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteAttendance + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteTimesheet: (id) => {
    const { baseApi, deleteTimesheet } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteTimesheet + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deletePayroll: (id) => {
    const { baseApi, deletePayroll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deletePayroll + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteSales: (id) => {
    const { baseApi, handleSales } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSales + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getAccountSetting: () => {
    const { baseApi, accountSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + accountSetting;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPayslipFormat: () => {
    const { baseApi, payslipFormat } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + payslipFormat;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getCompanySettings: (companyToken) => {
    const { baseApi, settings } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + settings;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken || token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  createCompanySettings: (payload, companyToken) => {
    const { baseApi, settings } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + settings;
    const params = payload;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken || token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  updateCompanySettings: (settingId, payload, companyToken) => {
    const { baseApi, settings } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}${settings}/${settingId}`;
    const params = payload;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken || token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  getTaxType: (country) => {
    const { baseApi, getTaxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}${getTaxType}/${country}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getExpenseType: (type) => {
    const { baseApi, getExpenseType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}${getExpenseType}?type=${type}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getSuppliersPurchase: (id) => {
    const { baseApi, getSuppliersPurchase } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getSuppliersPurchase}/${id}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAccountLeaveType: () => {
    const { baseApi, getAdminLeaveTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getAdminLeaveTypes;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAccountInfoLeaveType: (companyToken) => {
    const { baseApi, accountLeaveType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + accountLeaveType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getPayslipShow: (companyToken) => {
    const { baseApi, accountPayslipShow } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + accountPayslipShow;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getHoliday: (companyToken) => {
    const { baseApi, holiday } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + holiday;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getHolidayType: () => {
    const { baseUrl, holidayType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseUrl + holidayType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPensionType: () => {
    const { baseApi, handlePensionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + handlePensionType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handlePensionType: async (data) => {
    const { baseApi, handlePensionType } = ApiConfig;

    const url = baseApi + handlePensionType;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  createExpense: async (data, type, date, paymentMode, bankDetails, files) => {
    const { baseApi, createExpenses } = ApiConfig;

    const url = baseApi + createExpenses;
    const token = localStorage.getItem("accessToken");
    const params = {
      expType: type,
      amount: data?.amount?.value,
      date: date.format("YYYY-MM-DD"),
      paidVia: paymentMode,
      name: data?.name?.value,
      description: data?.description?.value,
      tax: data?.taxAmount?.value,
      images: files,
      bankName: bankDetails,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  addOtherIncome: async (data, type, date, paymentMode, bankDetails, files) => {
    const { baseApi, addOtherIncome } = ApiConfig;

    const url = baseApi + addOtherIncome;
    const token = localStorage.getItem("accessToken");
    const params = {
      incomeType: type,
      amount: data?.amount?.value,
      date: date.format("YYYY-MM-DD"),
      receivedVia: paymentMode,
      name: data?.name?.value,
      description: data?.description?.value,
      tax: data?.taxAmount?.value,
      images: files?.path,
      bankName: bankDetails,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  updateOtherIncome: async (
    id,
    data,
    type,
    date,
    paymentMode,
    bankDetails,
    files
  ) => {
    const { baseApi, updateOtherIncome } = ApiConfig;
    const url = baseApi + updateOtherIncome + "/" + id;
    const token = localStorage.getItem("accessToken");
    const params = {
      incomeType: type,
      amount: data?.amount?.value,
      date: date.format("YYYY-MM-DD"),
      receivedVia: paymentMode,
      name: data?.name?.value,
      description: data?.description?.value,
      tax: data?.taxAmount?.value,
      images: files,
      bankName: bankDetails,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  deleteOtherIncome: (id) => {
    const { baseApi, deleteIncome } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteIncome + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  handleAddTaxPeriod: async (start, end) => {
    const { baseApi, addTaxPeriod } = ApiConfig;

    const url = baseApi + addTaxPeriod;
    const token = localStorage.getItem("accessToken");

    const params = {
      start: start,
      end: end,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  getASDeductionType: (companyToken) => {
    const { baseApi, deductionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + deductionType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleASDeductionType: async (data) => {
    const { baseApi, deductionType } = ApiConfig;

    const url = baseApi + deductionType;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  deleteASDeductionType: (id) => {
    const { baseApi, deductionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deductionType + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deletePurchase: (id) => {
    const { baseApi, deletePurchase } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deletePurchase + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  updateASDeductionType: async (data, id) => {
    const { baseApi, deductionType } = ApiConfig;

    const url = baseApi + deductionType + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  updateExpense: async (
    id,
    data,
    type,
    date,
    paymentMode,
    bankDetails,
    files
  ) => {
    const { baseApi, updateExpense } = ApiConfig;
    const url = baseApi + updateExpense + "/" + id;
    const token = localStorage.getItem("accessToken");
    const params = {
      expType: type,
      amount: data?.amount?.value,
      date: date.format("YYYY-MM-DD"),
      paidVia: paymentMode,
      name: data?.name?.value,
      description: data?.description?.value,
      tax: data?.taxAmount?.value,
      images: files,
      bankName: bankDetails,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  deleteExpense: (id) => {
    const { baseApi, deleteExpense } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteExpense + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getASPayslip: (companyToken) => {
    const { baseApi, getPayslipSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getPayslipSetting;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleASPayslip: async (settings, payeName, data) => {
    const { baseApi, handlePayslipSetting } = ApiConfig;

    const url = baseApi + handlePayslipSetting;
    const token = localStorage.getItem("accessToken");

    const params = buildPayslipSettingPayload(settings, payeName, data);

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  getAdmAllowance: ({ country, start, end } = {}) => {
    const { baseUrl, allowanceType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const queryParams = [];
    if (country) {
      queryParams.push(`country=${encodeURIComponent(country)}`);
    }
    if (start) {
      queryParams.push(`start=${encodeURIComponent(start)}`);
    }
    if (end) {
      queryParams.push(`end=${encodeURIComponent(end)}`);
    }
    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
    const url = baseUrl + allowanceType + queryString;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAdmTaxTable: (taxCountry, frequency) => {
    const { baseApi, taxTable } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${taxTable}/?country=${taxCountry}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAdmFringe: () => {
    const { baseUrl, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseUrl + fringeType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAdmPension: () => {
    const { baseUrl, pensionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseUrl + pensionType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAdmTax: () => {
    const { baseApi, taxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + taxType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAdmLeave: (start, end, country) => {
    const { baseApi, getLeaveSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = {
      start: start,
      end: end,
      country: country
    };
    const url = baseApi + getLeaveSetting;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    console.log(url, params, headers);
    return ApiCallGet(url, params, headers);
  },
  getASFringeType: () => {
    const { baseApi, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + fringeType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAddiInfoFringeType: (companyToken) => {
    const { baseApi, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + fringeType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleASFringeType: async (data, isTaxable) => {
    const { baseApi, fringeType } = ApiConfig;

    const url = baseApi + fringeType;
    const token = localStorage.getItem("accessToken");
    const params = {
      name: data,
      isTaxable: isTaxable,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  deleteASFringeType: (id) => {
    const { baseApi, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + fringeType + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  updateASFringeType: async (data, id, fringeTax) => {
    const { baseApi, fringeType } = ApiConfig;

    const url = baseApi + fringeType + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
      isTaxable: fringeTax,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  getTaxYear: () => {
    const { baseApi, getTaxYear } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getTaxYear;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAddiInfoTaxYear: (companyToken) => {
    const { baseApi, getTaxYear } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getTaxYear;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleTaxYear: async (start, end) => {
    const { baseApi, handleTaxYear } = ApiConfig;

    const url = baseApi + handleTaxYear;
    const token = localStorage.getItem("accessToken");

    const params = {
      start: start,
      end: end,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  deleteTaxYear: (id) => {
    const { baseApi, handleTaxYear } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleTaxYear + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  updateTaxYear: async (start, end, id) => {
    const { baseApi, handleTaxYear } = ApiConfig;

    const url = baseApi + handleTaxYear + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      start: start,
      end: end,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  handleAccountLeaveType: async (leavedata, isPaid) => {
    const { baseApi, accountLeaveType } = ApiConfig;

    const url = baseApi + accountLeaveType;
    const token = localStorage.getItem("accessToken");

    const params = {
      type: leavedata,
      isPaid: isPaid,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  handleShowPayslip: async (data) => {
    const { baseApi, accountPayslipShow } = ApiConfig;
    const url = baseApi + accountPayslipShow;
    const token = localStorage.getItem("accessToken");

    const params = {
      companyEmail: data.companyEmail,
      payslipNumber: data.payslipNumber,
      leaveDays: data.leaveDays,
      allDeductions: data.allDeductions,
      empPassNo: data.empPassNo,
      staffId: data.staffId,
      joiningDate: data.joiningDate,
      payFrequency: data.payFrequency,
      department: data.isdepartment,
      employeeEmail: data.employeeEmail,
      employeeAddress: data.employeeAddress,
      jobposition: data.jobposition,
      bonus: data.bonus,
      companyAddress: data.companyAddress,
      totalTax: data.totalTax,
      uif: data.uif,
      sdl: data.sdl,
      commission: data.commission,
      isDeleted: data.isDeleted,
      overTime: data.overTime,
      doubleOverTime: data.doubleOverTime,
      extraTime: data.extraTime,
      overTimeHours: data.overTimeHours,
      doubleOverTimeHours: data.doubleOverTimeHours,
      extraTimeHours: data.extraTimeHours,
      totalPension: data.totalPension,
      totalDeductions: data.employeeAddress,
      totalFringeBenefits: data.totalFringeBenefits,
      totalLevies: data.totalLevies,
      totalMedical: data.totalMedical,
      totalAllowances: data.totalAllowances,
      companyPhone: data.companyPhone,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  handleShowPayslipEmptyValues: async (data) => {
    const { baseApi, accountPayslipShowEmptyValues } = ApiConfig;
    const url = baseApi + accountPayslipShowEmptyValues;
    const token = localStorage.getItem("accessToken");

    const params = {
      showEmptyValues: data.showEmptyValues,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  handleHoliday: async (start, end, type, desc) => {
    const { baseApi, holiday } = ApiConfig;

    const url = baseApi + holiday;
    const token = localStorage.getItem("accessToken");

    const params = {
      start: start,
      end: end,
      type: type,
      desc: desc,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  handleScheduleTime: async (week) => {
    const { baseApi, getSchedule } = ApiConfig;

    const url = baseApi + getSchedule;
    const token = localStorage.getItem("accessToken");

    const params = {
      week,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  updateLeaveType: async (leave, id, isPaid) => {
    const { baseApi, accountLeaveType } = ApiConfig;

    const url = baseApi + accountLeaveType + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      type: leave,
      isPaid: isPaid,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  updateHoliday: async (start, end, type, desc, id) => {
    const { baseApi, holiday } = ApiConfig;

    const url = baseApi + holiday + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      start: start,
      end: end,
      type: type,
      desc: desc,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  updatePensionType: async (data, id) => {
    const { baseApi, handlePensionType } = ApiConfig;

    const url = baseApi + handlePensionType + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  deleteAccountLeaveType: (id) => {
    const { baseApi, accountLeaveType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + accountLeaveType + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteHoliday: (id) => {
    const { baseApi, holiday } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + holiday + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deletePensionType: (id) => {
    const { baseApi, handlePensionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handlePensionType + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  getAccountUnitType: (companyToken) => {
    const { baseApi, unitType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + unitType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getDepartment: () => {
    const { baseApi, handleDepartment } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + handleDepartment;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAddiInfoDepartment: (companyToken) => {
    const { baseApi, handleDepartment } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + handleDepartment;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getOperators: (page = 1, limit = 10, isActive = true, companyToken) => {
    const { baseApi, operator } = ApiConfig;
    const token = companyToken || localStorage.getItem("accessToken");

    const url = baseApi + operator;
    const params = { page, limit, isActive };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  createOperator: async (data) => {
    const { baseApi, operator } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + operator;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, data, headers);
  },
  updateOperator: async (id, data) => {
    const { baseApi, operator } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + operator + "/" + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, data, headers);
  },
  deleteOperator: (id) => {
    const { baseApi, operator } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + operator + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  getOperatorEmployees: (companyToken) => {
    const { baseApi, operatorEmployees } = ApiConfig;
    const token = companyToken || localStorage.getItem("accessToken");

    const url = baseApi + operatorEmployees;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getPayslipTaxyear: () => {
    const { baseApi, getPayslipTaxyear } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getPayslipTaxyear;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getTaxyear: (id) => {
    const { baseApi, getTaxyear } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getTaxyear + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPayslipTaxyearList: (id) => {
    const { baseApi, getPayslipTaxyear } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getPayslipTaxyear + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getAllDepartmentList: () => {
    const { baseApi, getAllDepartmentList } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getAllDepartmentList;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getCompantList: () => {
    const { baseApi, getCompanyList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getCompanyList;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getPayslipSetting: () => {
    const { baseApi, payslipSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + payslipSetting;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleAccountUnitType: async (data) => {
    const { baseApi, handleUnit } = ApiConfig;

    const url = baseApi + handleUnit;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  handleDepartment: async (data, active) => {
    const { baseApi, handleDepartment } = ApiConfig;

    const url = baseApi + handleDepartment;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: data,
      isActive: active,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  updateUnitType: async (unit, id) => {
    const { baseApi, handleUnit } = ApiConfig;

    const url = baseApi + handleUnit + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: unit,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  updateDepartment: async (department, active, id) => {
    const { baseApi, handleDepartment } = ApiConfig;

    const url = baseApi + handleDepartment + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: department,
      isActive: active,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  deleteDepartment: (id) => {
    const { baseApi, handleDepartment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleDepartment + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteAccountUnitType: (id) => {
    const { baseApi, handleUnit } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleUnit + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  accountSettings: async (
    detail,
    data,
    mobile,
    cCode,
    phone,
    phoneCode,
    imgUrl,
    favUrl,
    deskNoti,
    emailNoti,
    orders,
    vatAndGst,
    selectedCurrency,
    format
  ) => {
    const { baseApi, accountSetting } = ApiConfig;
    const url = baseApi + accountSetting;
    const token = localStorage.getItem("accessToken");

    const params = {
      companyName: detail?.companyName?.value || data?.companyName,
      email: detail?.email?.value || data?.email,
      cCode: cCode || data?.cCode || "+27",
      cellNumberCCode: phoneCode || data?.cellNumberCCode || "+27",
      phone: mobile || data?.phone,
      address1: detail?.addressLine1?.value || data?.address1,
      address2: detail?.addressLine2?.value || data?.address2,
      city: detail?.city?.value || data?.city,
      state: detail?.states?.value || data?.state || "",
      zipCode: detail?.zipCode?.value || data?.zipCode || "",
      industry: detail?.industry?.value || data?.industry,
      country: detail?.country?.value || data?.country,
      region: detail?.region?.value || data?.region,
      incomeTax: detail?.incomeTax?.value || data?.incomeTax,
      cellNumber: phone || data?.cellNumber,

      companyRegNo:
        detail?.companyRegistrationNumber?.value || data?.companyRegNo,
      currency: selectedCurrency || data?.currency,

      [vatAndGst === "vatNumber" ? "vatNo" : "gstNo"]:
        detail?.number?.value || data?.vatNo || data?.gstNo || "",
      logo: imgUrl[0] || data?.logo,
      favicon: favUrl[0] || data?.favicon,
      isDesktopNoti: deskNoti ? deskNoti : data?.isDesktopNoti,
      isEmailNoti: emailNoti ? emailNoti : data?.isEmailNoti,
      isOreders: orders ? orders : data?.isOreders,
      payslipFormat: {
        name: format || data?.payslipFormat?.name,
      },
      // isDeleted:data?.isDeleted?.value,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },
  accountSettingsWithNewToken: async (
    detail,
    data,
    phone,
    phoneCode,
    imgUrl,
    favUrl,
    deskNoti,
    emailNoti,
    orders,
    vatAndGst,
    selectedCurrency,
    format,
    companyToken
  ) => {
    const { baseApi, accountSetting } = ApiConfig;
    const url = baseApi + accountSetting;
    const token = localStorage.getItem("accessToken");

    const params = {
      companyName: detail?.companyName?.value || data?.companyName,
      email: detail?.email?.value || data?.email,
      cCode: phoneCode || data?.cCode || "+27",
      cellNumberCCode: phoneCode || data?.cellNumberCCode || "+27",
      address1: detail?.addressLine1?.value || data?.address1,
      address2: detail?.addressLine2?.value || data?.address2,
      city: detail?.city?.value || data?.city,
      state: detail?.states?.value || data?.state || "",
      zipCode: detail?.zipCode?.value || data?.zipCode || "",
      industry: detail?.industry?.value || data?.industry,
      country: detail?.country?.value || data?.country,
      region: detail?.region?.value || data?.region,
      incomeTax: detail?.incomeTax?.value || data?.incomeTax,
      cellNumber: phone || data?.cellNumber,

      companyRegNo:
        detail?.companyRegistrationNumber?.value || data?.companyRegNo,
      currency: selectedCurrency || data?.currency,

      [vatAndGst === "vatNumber" ? "vatNo" : "gstNo"]:
        detail?.number?.value || data?.vatNo || data?.gstNo || "",
      logo: imgUrl[0] || data?.logo,
      favicon: favUrl[0] || data?.favicon,
      isDesktopNoti: deskNoti ? deskNoti : data?.isDesktopNoti,
      isEmailNoti: emailNoti ? emailNoti : data?.isEmailNoti,
      isOreders: orders ? orders : data?.isOreders,
      payslipFormat: {
        name: format || data?.payslipFormat?.name,
      },
      // isDeleted:data?.isDeleted?.value,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };

    return ApiCallPut(url, params, headers);
  },

  NewCompany: async (
    detail,
    data,
    phone,
    phoneCode,
    imgUrl,
    favUrl,
    deskNoti,
    emailNoti,
    orders,
    vatAndGst,
    selectedCurrency,
    format
  ) => {
    const { baseApi, accountSetting } = ApiConfig;
    const url = baseApi + accountSetting;
    const token = localStorage.getItem("accessToken");

    const params = {
      companyName: detail?.companyName?.value || data?.companyName,
      email: detail?.email?.value || data?.email,
      cCode: phoneCode || data?.cCode || "+27",
      cellNumberCCode: phoneCode || data?.cellNumberCCode || "+27",
      address1: detail?.addressLine1?.value || data?.address1,
      address2: detail?.addressLine2?.value || data?.address2,
      city: detail?.city?.value || data?.city,
      state: detail?.states?.value || data?.states || "",
      zipCode: detail?.zipCode?.value || data?.zipCode || "",
      industry: detail?.industry?.value || data?.industry,
      country: detail?.country?.value || data?.country,
      region: detail?.region?.value || data?.region,
      cellNumber: phone || data?.cellNumber,
      incomeTax: detail?.incomeTax?.value || data?.incomeTax,
      companyRegNo:
        detail?.companyRegistrationNumber?.value || data?.companyRegNo,
      currency:
        {
          value: selectedCurrency?.value,
          label: selectedCurrency?.label,
        } || data?.currency,

      [vatAndGst === "vatNumber" ? "vatNo" : "gstNo"]:
        detail?.number?.value || data?.vatNo || data?.gstNo || "",
      logo: imgUrl[0] || data?.logo,
      favicon: favUrl[0] || data?.favicon,
      isDesktopNoti: deskNoti ? deskNoti : data?.isDesktopNoti,
      isEmailNoti: emailNoti ? emailNoti : data?.isEmailNoti,
      isOreders: orders ? orders : data?.isOreders,
      payslipFormat: {
        name: format,
      },

      // isDeleted:data?.isDeleted?.value,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  deleteProject: (id) => {
    const { baseApi, project } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + project + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteItems: (id) => {
    const { baseApi, deleteItem } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteItem + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteBank: (id) => {
    const { baseApi, getOneBank } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getOneBank + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  getOtherExpense: (page) => {
    const { baseApi, getOtherExpenses } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    const url = `${baseApi}${getOtherExpenses}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getOtherIncome: (page) => {
    const { baseApi, getOtherIncome } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    if (page) params.append("page", page);
    const url = `${baseApi}${getOtherIncome}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getSupplier: (text, sort, page) => {
    const { baseApi, getSuppliers } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (page) params.append("page", page);
    if (sort) params.append("sort", sort);

    const url = `${baseApi}${getSuppliers}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getDeduction: (text, sort, page) => {
    const { baseApi, getDeduction } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getDeduction}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getLoan: (text, sort, page) => {
    const { baseApi, getLoans } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getLoans}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getStartPeriod: (id) => {
    const { baseApi, getStartPeriod } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getStartPeriod + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getLoanTransaction: (text, sort, page, id) => {
    const { baseApi, getLoansTranasaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();
    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getLoansTranasaction}${id ? `/${id}` : ""}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getDeductionTransaction: (text, sort, page, id) => {
    const { baseApi, getDeductionTranasaction } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (sort) params.append("sort", sort);
    if (page) params.append("page", page);

    const url = `${baseApi}${getDeductionTranasaction}${id ? `/${id}` : ""}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  addDeductionManualPayment: (deductionId, employeeId, amount, date, paymentMethod) => {
    const { baseApi, deductionManualPayment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${deductionManualPayment}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const parameters = {
      deductionId,
      employeeId,
      amount,
      date,
      paymentMethod,
    };
    return ApiCallPost(url, parameters, headers);
  },
  addLoanManualPayment: (loanId, employeeId, amount, date) => {
    const { baseApi, loanManualPayment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${loanManualPayment}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const parameters = {
      loanId,
      employeeId,
      amount,
      date,
    };
    return ApiCallPost(url, parameters, headers);
  },
  getCustomer: (text, sort, page) => {
    const { baseApi, getCustomers } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();

    if (text) params.append("search", text);
    if (page) params.append("page", page);
    if (sort) params.append("sort", sort);

    const url = `${baseApi}${getCustomers}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getStatementGenerate: (startDate, endDate, bankName, type, sortValue) => {
    const { baseApi, getStatementGenerate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const sort = sortValue === "name" ? "amount" : sortValue;
    const start = startDate?.format("YYYY-MM-DD");
    const end = endDate?.format("YYYY-MM-DD");
    const params = new URLSearchParams();

    if (start) params.append("start", start);
    if (end) params.append("end", end);
    if (bankName) params.append("bankName", bankName);
    if (type) params.append("type", type);
    if (sort) params.append("sort", sort);

    const url = `${baseApi}${getStatementGenerate}${params.toString() ? `/?${params.toString()}` : ""
      }`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  deleteSuppliers: (id) => {
    const { baseApi, handleSupplier } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSupplier + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteDeduction: (id) => {
    const { baseApi, handleDeduction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleDeduction + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteLoan: (id) => {
    const { baseApi, handleLoan } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLoan + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteCustomers: (id) => {
    const { baseApi, handleCustomer } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCustomer + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteBill: (id) => {
    const { baseApi, handleBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleBill + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteInvoice: (id) => {
    const { baseApi, handleInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleInvoice + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getInvoiceDetail: (invoiceId) => {
    const { baseApi, handleInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleInvoice + "/" + invoiceId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  handleSupplier: (payload) => {
    const { baseApi, handleSupplier } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSupplier;
    const params = {
      ...payload,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleDownloadBill: (id) => {
    const { baseApi, downloadBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + downloadBill + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },

  sendBill: (id) => {
    const { baseApi, sendBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + sendBill + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleDownloadInvoice: (id) => {
    const { baseApi, downloadInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + downloadInvoice + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleDownloadReceipt: (id) => {
    const { baseApi, downloadCashReceipt } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + downloadCashReceipt + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleSentReceipt: (id) => {
    const { baseApi, handleSentReceipt } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSentReceipt + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  getReceiptView: (id) => {
    const { baseApi, getReceiptView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getReceiptView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleCashFlowStatement: (month, year, viewType, format) => {
    const { baseApi, cashFlowDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${cashFlowDownload}?month=${month}&year=${year}&viewType=${viewType}&format=${format}`;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  statementDownload: (start, end, type, bank, format) => {
    const { baseApi, getStatementDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getStatementDownload;

    const params = {
      start: start,
      end: end,
      type: type,
      bankName: bank,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleCustomer: (data) => {
    const { baseApi, handleCustomer } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCustomer;
    const params = {
      name: data?.name,
      email: data?.email,
      companyName: data?.companyName,
      phone: data?.phone,
      taxId: data?.taxId,
      creditLimits: data?.creditLimits,
      isBlocked: data?.isBlocked,
      billingAddress: data?.billingAddress,
      shippingAddress: data?.shippingAddress,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  handleInvoice: (data) => {
    const { baseApi, handleInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleInvoice;
    const params = {
      invoiceType: data.invoiceType,
      customer: data.customer,
      billingAddress: data.billingAddress,
      shippingAddress: data.shippingAddress,
      paymentTerms: data.paymentTerms,
      dailyPenaltyAmount: data.dailyPenaltyAmount,
      otherInformation: data.otherInformation,
      productInformation: data.productInformation,
      discountPercentage: data.discountPercentage,
      discountValue: data.discountValue,
      taxPercentage: data.taxPercentage,
      taxValue: data.taxValue,
      total: data.total,
      grandTotal: data.grandTotal,
    };

    if (data.invoiceType === "One-Time") {
      params.dueDate = data.dueDate;
      params.invoiceDate = data.invoiceDate;
    } else {
      params.profileName = data.profileName;
      params.repeatEvery = data.repeatEvery;
      params.startOn = data.startOn;
      params.endOn = data.endOn;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleBill: (payload) => {
    const { baseApi, handleBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleBill;
    const params = { ...payload };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  updateBill: (payload, id) => {
    const { baseApi, handleBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleBill + "/" + id;
    const params = { ...payload };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleDeduction: (
    employeeId,
    type,
    amount,
    typeD,
    salary,
    desc,
    fromDate,
    toDate,
    paymentMethod
  ) => {
    const { baseApi, handleDeduction } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleDeduction;
    const params = {
      employee: employeeId,
      deductions: [
        {
          type: type,
          rateValue: amount,
          payType: typeD,
          deductType: salary === "" ? "Salary" : salary,
          desc: desc,
          start: fromDate,
          end: toDate,
          paymentMethod: paymentMethod,
        },
      ],
      // otherInfo: data?.optional?.value || "N/A",
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    console.log(url,params)
    return ApiCallPost(url, params, headers);
  },
  handleSale: (saleName, startDate, endDate, product) => {
    const { baseApi, addSpecialItem } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addSpecialItem;
    const params = {
      sale_name: saleName,
      start: startDate?.format("YYYY-MM-DD"),
      end: endDate?.format("YYYY-MM-DD"),
      products: product,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  sentInvoice: (id) => {
    const { baseApi, sentInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + sentInvoice + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  paidInvoice: (id, bankId, type, date, amount, reference) => {
    const { baseApi, paidInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + paidInvoice + "/" + id;
    const params = {
      type: type,
      bankId: type === "Bank" ? bankId : "",
      date: date || dayjs().toISOString(),
      amount: amount,
      reference: reference || "",
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  paidBillInvoice: (invoiceId, payload) => {
    const { baseApi, billPaid } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + billPaid + "/" + invoiceId;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },
  // For updating existing payment
  updateBillPayment: (invoiceId, transactionId, payload) => {
    const { baseApi, handleBill } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url =
      baseApi + handleBill + "/" + invoiceId + "/payment/" + transactionId;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  deleteInvoiceTransaction: (invoiceId, transactionId) => {
    const { baseApi, handleInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url =
      baseApi + handleInvoice + "/" + invoiceId + "/payment/" + transactionId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  handleLoan: (
    employeeId,
    date,
    repayment,
    loan,
    repaymentAmount,
    numberRepayments,
    des,
    remark,
    paymentMethod
  ) => {
    const { baseApi, handleLoan } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLoan;
    const params = {
      employee: employeeId,
      taxYear: date,
      repayStart: repayment,
      desc: des,
      loanAmt: loan,
      repayAmt: repaymentAmount,
      numRepay: numberRepayments,
      remark: remark,
      paymentMethod: paymentMethod,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleEmployeeReport: (employee, costCente, empId, format, extraFilters = {}) => {
    const { baseApi, handleEmployeeReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleEmployeeReport;
    const isActive = employee === "true";
    const isDeleted = employee === "deleted" ? true : false;

    const params = {
      isActive: isActive,
      isDeleted: isDeleted,
      department: costCente,
      role: extraFilters?.role || "",
      search: extraFilters?.search || "",
      startDate: extraFilters?.startDate || "",
      endDate: extraFilters?.endDate || "",
      format: format,
      stafId: empId,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },

  handlePayslipReport: (paymentMethod, department, start, end, format) => {
    const { baseApi, handlePayslipReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handlePayslipReport;

    const params = {
      paymentMethod,
      department,
      start,
      end,
      format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };

    return ApiCallPost(url, params, headers, "blob");
  },

  handleEmpPayslipReport: (email, start, end, format) => {
    const { baseApi, handleEmpPayslipReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleEmpPayslipReport;

    const params = {
      email: email,
      start: start,
      end: end,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },

  handlePensionReport: (start, end, pension, format) => {
    const { baseApi, handlePensionReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handlePensionReport;

    const params = {
      start: start,
      end: end,
      pension: pension,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handlePayeReport: (start, end, format) => {
    const { baseApi, handlePayeReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handlePayeReport;

    const params = {
      start: start,
      end: end,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleGeneralizeTax: (start, end, format) => {
    const { baseApi, handleGeneralizeTax } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleGeneralizeTax;

    const params = {
      start: start,
      end: end,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleSdlReport: (start, end, format) => {
    const { baseApi, handleSdlReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSdlReport;

    const params = {
      start: start,
      end: end,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleUifReport: (start, end, format) => {
    const { baseApi, handleUifReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleUifReport;

    const params = {
      start: start,
      end: end,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleAttendanceReport: (start, end, includeAllEmployees, format, filters) => {
    const { baseApi, handleAttendanceReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleAttendanceReport;

    const params = {
      fromDate: start,
      toDate: end,
      isShowAbsentEmp: includeAllEmployees,
      format: format,
      ...bulkReportFilters(filters),
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handlePayrollDepartmemt: (department) => {
    const { baseApi, handlePayrollDepartment } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url =
      baseApi + handlePayrollDepartment + "/?department=" + department;

    const params = {
      // department: department
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleLeviesReport: (email, start, end, levy, format, filters) => {
    const { baseApi, handleLeviesReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLeviesReport;

    const params = {
      ...(email ? { email: email } : {}),
      start: start,
      end: end,
      levy: levy,
      format: format,
      ...bulkReportFilters(filters),
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleDeductionReport: (deduction, start, end, department, format) => {
    const { baseApi, handleDeductionReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleDeductionReport;

    const params = {
      deduction: deduction,
      start: start,
      end: end,
      department: department,
      format: format,
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleSalaryReport: (id) => {
    const { baseApi, handleSalaryReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSalaryReport + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  exportPayslip: (salaryId) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}salary_print/${salaryId}`;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleFringeReport: (start, end, fringe, format, filters) => {
    const { baseApi, handleFringeReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleFringeReport;

    const params = {
      start: start,
      end: end,
      fringe: fringe,
      format: format,
      ...bulkReportFilters(filters),
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleCreditReport: (start, end, credit, format, filters) => {
    const { baseApi, handleCreditReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCreditReport;

    const params = {
      start: start,
      end: end,
      creditType: credit,
      format: format,
      ...bulkReportFilters(filters),
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  handleLoanReport: (start, end, format, filters) => {
    const { baseApi, handleLoanReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleLoanReport;

    const params = {
      start: start,
      end: end,
      format: format,
      ...bulkReportFilters(filters),
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:
        format === "pdf"
          ? "application/pdf"
          : format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
    };
    return ApiCallPost(url, params, headers, "blob");
  },

  handlePurchase: (
    supplierName,
    invoicesDate,
    duesDate,
    product,
    discount,
    tax,
    billNumber,
    category,
    paymentStatus,
    paymentMode,
    remarks,
    orderNumber,
    grandTotal,
    totalAmount,
    bankDetails
  ) => {
    const { baseApi, handlePurchase } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handlePurchase;
    const params = {
      supplier: supplierName?.supplierId,
      supplierName: supplierName?.supplierName,
      billDate: invoicesDate,
      dueDate: duesDate,
      billNumber: billNumber,
      category: category,
      orderNumber: orderNumber,
      item: product,
      total: Number(totalAmount),
      tax: Number(tax),
      discount: Number(discount),
      grandTotal: Number(grandTotal),
      paymentStatus: paymentStatus,
      paymentMode: paymentMode,
      remarks: remarks,
      bankDetails: bankDetails,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleEditPurchase: (
    id,
    supplierName,
    invoicesDate,
    duesDate,
    billNumber,
    category,
    orderNumber,
    product,
    discount,
    tax,
    paymentStatus,
    paymentMode,
    remarks,
    totalAmount,
    grandTotal,
    bankDetails
  ) => {
    const { baseApi, purchase } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + purchase + id;
    const params = {
      supplierName: supplierName?.supplierName,
      supplier: supplierName?.supplierId,
      billDate: invoicesDate,
      dueDate: duesDate,
      billNumber: billNumber,
      category: category,
      orderNumber: orderNumber,
      item: product,
      total: Number(totalAmount),
      tax: Number(tax),
      discount: Number(discount),
      grandTotal: Number(grandTotal),
      paymentStatus: paymentStatus,
      paymentMode: paymentMode,
      remarks: remarks,
      bankDetails: bankDetails,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleSales: (
    salesOrder,
    expiryDate,
    Opportunity,
    description,
    product,
    subtotal,
    grandTotal,
    currency,
    exchangeRate,
    tax,
    totalAmount
  ) => {
    const { baseApi, handleSales } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSales;
    const params = {
      item: product,
      total: Number(totalAmount),
      tax: Number(tax),
      grandTotal: Number(grandTotal),
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  updateSupplier: (data, id) => {
    const { baseApi, handleSupplier } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleSupplier + "/" + id;
    const params = { ...data };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  updateCustomer: (data, id, user, mobile, cCode) => {
    const { baseApi, handleCustomer } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCustomer + "/" + id;

    const params = {
      name: data?.name.value || user?.name,
      email: data?.email.value || user?.email,
      phone: mobile || user?.phone,
      cCode: cCode,
      address: data?.address.value || user?.address,
      city: data?.city.value || user?.city,
      country: data?.country.value || user?.country,
      ZIPCode: data?.zipCode.value || user?.ZIPCode,
      reward: data?.rewardPoint.value || user?.reward,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  updateInvoice: (invoiceId, data) => {
    const { baseApi, handleInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = {
      customer: data.customer,
      billingAddress: data.billingAddress,
      shippingAddress: data.shippingAddress,
      paymentTerms: data.paymentTerms,
      dailyPenaltyAmount: data.dailyPenaltyAmount,
      otherInformation: data.otherInformation,
      productInformation: data.productInformation,
      discountPercentage: data.discountPercentage,
      discountValue: data.discountValue,
      taxPercentage: data.taxPercentage,
      taxValue: data.taxValue,
      total: data.total,
      grandTotal: data.grandTotal,
      invoiceDate: data.invoiceDate,
      dueDate: data.dueDate,
    };

    const url = baseApi + handleInvoice + "/" + invoiceId;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleAbsent: async (id) => {
    const { baseApi, markAbsent } = ApiConfig;

    const url = baseApi + markAbsent + `/${id}`;
    const token = localStorage.getItem("accessToken");

    const params = {};

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },
  defaultBank: async (id) => {
    const { baseApi, defaultBank } = ApiConfig;

    const url = baseApi + defaultBank + "/" + id;
    const token = localStorage.getItem("accessToken");

    const params = {};

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, params, headers);
  },

  getApprovedCommission: () => {
    const { baseApi, approveAllCommission } = ApiConfig;
    const url = baseApi + approveAllCommission;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  getApprovedTravelAllowance: () => {
    const { baseApi, approveAllTravelAllowance } = ApiConfig;
    const url = baseApi + approveAllTravelAllowance;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleCompanyDetails: (id) => {
    const { baseApi, companyDetails } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + companyDetails + `/${id}`;

    const params = {
      // department: department
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  CreateAllowance: async (leave, isPaid, start, end, country) => {
    const { baseApi, createAllowance } = ApiConfig;

    const url = baseApi + createAllowance;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: leave,
      isTaxable: isPaid,
    };

    // Add start and end dates if provided
    if (start) {
      params.start = start;
    }
    if (end) {
      params.end = end;
    }
    // Add country only if it's a non-empty string
    if (country && country.trim() !== "") {
      params.country = country;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  EditAllowance: async (allowanceText, allowanceTax, id) => {
    const { baseApi, createAllowance } = ApiConfig;

    const url = baseApi + createAllowance + "/?id=" + id;
    const token = localStorage.getItem("accessToken");

    const params = {
      name: allowanceText,
      isTaxable: allowanceTax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, params, headers);
  },

  deleteAllowance: (id) => {
    const { baseApi, createAllowance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + createAllowance + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  createFringe: (name, tax) => {
    const { baseUrl, fringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${fringeType}`;
    const params = {
      name: name,
      isTaxable: tax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  deleteFringe: (id) => {
    const { baseApi, DeletefringeType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + DeletefringeType + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteSupPension: (id) => {
    const { baseApi, DeletepensionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + DeletepensionType + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  createPansion: (name, tax) => {
    const { baseUrl, pensionType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${pensionType}`;
    const params = {
      name: name,
      isTaxable: tax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  createSupLeave: (payload) => {
    const { baseApi, handleLeaveSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleLeaveSetting}`;
    const params = {
      type: payload.type,
      method: payload.method,
      rate: payload.rate,
      yearlyCap: payload.yearlyCap,
      carryToNextFY: payload.carryToNextFY,
      country: payload.country,
      start: payload.start,
      end: payload.end,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  deleteLeave: (id) => {
    const { baseApi, handleLeaveSetting } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleLeaveSetting}/${id}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteLeaveRequest: (id) => {
    const { baseApi, handleDeleteLeave } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleDeleteLeave}/${id}`;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getUif: (countryTax) => {
    const { baseApi, uifGet } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + uifGet + "/?country=" + countryTax;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  deleteUif: (id) => {
    const { baseApi, uifDelete } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + uifDelete + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  getSdl: (sdlCountry) => {
    const { baseApi, sdlGet } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + sdlGet + "/?country=" + sdlCountry;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  deleteSdl: (id) => {
    const { baseApi, sdlDelete } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + sdlDelete + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteTextTable: (taxTableId, slabId) => {
    const { baseApi, deleteTextTabels } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteTextTabels + "/" + taxTableId;

    const params = {
      taxSlabId: slabId,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteAllTextTable: (taxTableId, slabId) => {
    const { baseApi, deleteAllTextTabels } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteAllTextTabels + "/" + taxTableId;

    const params = {
      taxSlabIds: slabId,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  createUif: (fixed, rate, countryTax, datefrom, dateTo, maxCap) => {
    const { baseApi, createUif } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createUif}`;
    const params = {
      country: countryTax,
      employerUIF: Number(rate),
      startYear: datefrom,
      endYear: dateTo,
      employeeUIF: Number(fixed),
      maxCap: Number(maxCap),
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  EditUif: (selectedId, fixed, rate, countryTax, datefrom, dateTo, maxCap) => {
    const { baseApi, createUif } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createUif}/?id=${selectedId} `;
    const params = {
      country: countryTax,
      employerUIF: Number(rate),
      startYear: datefrom,
      endYear: dateTo,
      employeeUIF: Number(fixed),
      maxCap: Number(maxCap),
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  CreateSdl: (min, country, datefrom, dateTo) => {
    const { baseApi, createSdl } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSdl}`;
    const params = {
      country: country,
      employerSDL: Number(min),
      startYear: datefrom,
      endYear: dateTo,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  EditSdl: (id, min, sdlCountry, datefrom, dateTo) => {
    const { baseApi, createSdl } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSdl}/?id=${id}`;
    const params = {
      country: sdlCountry,
      employerSDL: Number(min),
      startYear: datefrom,
      endYear: dateTo,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  getContributionsType: () => {
    const { baseApi, contributionsType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + contributionsType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAddiInfoContributionsType: (companyToken) => {
    const { baseApi, contributionsType } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + contributionsType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getAdminContributionTypes: () => {
    const token = localStorage.getItem("accessToken");
    const { baseApi, getAdminContributionsType } = ApiConfig;

    const url = baseApi + getAdminContributionsType;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  addCreditsType: (creditTypeName, country, includeBenificiary, contribution, contributionTypeId, start, end, frequency) => {
    const { baseApi, getCreditTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getCreditTypes;

    const params = {
      name: creditTypeName,
      country: country,
      // includes_beneficiary: includeBenificiary,
    };

    // Add linked_contributions if provided
    if (contribution && contributionTypeId) {
      params.linked_contributions = contributionTypeId;
    }

    // Add start, end, and frequency if provided
    if (start) {
      params.start = start;
    }
    if (end) {
      params.end = end;
    }
    if (frequency) {
      params.frequency = frequency;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  getCreditsType: (country) => {
    const { baseApi, getCreditTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getCreditTypes + "/?country=" + country;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  // GET /credit_type/?country=...&start=...&end=... - Get credit types by country and date range
  getCreditsTypeByDateRange: async (country, start, end) => {
    const { baseApi, getCreditTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    // Format dates as DD-MM-YYYY
    const startFormatted = start; // Already formatted as DD-MM-YYYY
    const endFormatted = end; // Already formatted as DD-MM-YYYY

    const url = `${baseApi}${getCreditTypes}/?country=${encodeURIComponent(country)}&start=${startFormatted}&end=${endFormatted}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAddiInfoCreditsType: (companyToken) => {
    const { baseApi, getCreditTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getCreditTypes;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAllCreditsType: () => {
    const { baseApi, getCreditTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getCreditTypes;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },


  updateCreditsType: (creditTypeId, country, contribution, contributionType, name) => {
    const { baseApi, getCreditTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getCreditTypes + "/" + creditTypeId;
    const params = {
      country: country,
    };
    // Add name if provided
    if (name) {
      params.name = name;
    }
    // Add linked_contributions if provided
    if (contribution && contributionType) {
      params.linked_contributions = contributionType;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  deleteCreditsType: (creditTypeId) => {
    const { baseApi, getCreditTypes } = ApiConfig;

    const token = localStorage.getItem("accessToken");
    const url = baseApi + getCreditTypes + "/" + creditTypeId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },


  addEmployeeCredits: (data) => {
    const { baseApi, addEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addEmployeeCredits;
    const params = {
      ...data,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  updateEmployeeCredits: (data, id) => {
    const { baseApi, addEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addEmployeeCredits + "/" + id;
    const params = {
      ...data,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  getEmployeeCredits: (id) => {
    const { baseApi, getEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getEmployeeCredits + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getAllCredits: (start, end, type) => {
    const { baseApi, getAllCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const query = new URLSearchParams({
      start,
      end,
      type,
    }).toString();

    const url = `${baseApi}${getAllCredits}?${query}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  getAllCreditsList: () => {
    const { baseApi, getAllCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getAllCredits}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  deleteEmployeeCredits: (id) => {
    const { baseApi, deleteEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteEmployeeCredits + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  // POST /credit - Rate per Beneficiary Type
  // If id is provided, it will be passed as query parameter (?id={id}) for update
  postCreditBeneficiaries: async (data, id) => {
    const { baseApi, addEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    let url = baseApi + addEmployeeCredits;

    // If id exists, append it as query parameter for update
    if (id) {
      url += `?id=${id}`;
    }

    const params = {
      ...data,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const result = await ApiCallPost(url, params, headers);
    return result;
  },
  // GET /credit/?credit_type=... - Get credit beneficiaries
  getCreditBeneficiaries: async (creditTypeId) => {
    const { baseApi, addEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${addEmployeeCredits}/?credit_type=${creditTypeId}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  updateCredits: (data, id) => {
    const { baseApi, getCreditById } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getCreditById}/${id}`;
    const params = {
      ...data,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  getAgeGroups: (country, text, sort, page) => {
    const { baseApi, ageGroup } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (country) params.append("country", country);
    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${ageGroup}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    // const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getLeaveBalanceRate: () => {
    const { baseApi, leaveBalanceRate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + leaveBalanceRate;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  manageLeaveBalance: (action, payload) => {
    const { baseApi, manageLeaveBalance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${manageLeaveBalance}?action=${action}`;
    const params = {
      employee: payload.employee,
      leave_type: payload.leave_type,
      numberOfDays: Number(payload.numberOfDays),
      notes: payload.notes || null,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  createEmployeeLeaveBalance: (payload) => {
    const { baseApi, employeeLeavesBalance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${employeeLeavesBalance}`;
    const params = {
      employee: payload.employee,
      start: payload.start || null,
      end: payload.end || null,
      method: payload.method || null,
      isApplicable: payload.isApplicable !== undefined ? payload.isApplicable : null,
      leave_type: payload.leave_type || null,
      yearlyCap: payload.yearlyCap || null,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  getEmployeeLeaveBalances: (params = {}) => {
    const { baseApi, employeeLeavesBalances } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.search) queryParams.append("search", params.search);
    if (params.sort) queryParams.append("sort", params.sort);
    if (params.employee) queryParams.append("employee", params.employee);
    if (params.ageGroup) queryParams.append("ageGroup", params.ageGroup);
    if (params.frequency) queryParams.append("frequency", params.frequency);
    if (params.country) queryParams.append("country", params.country);
    if (params.id) queryParams.append("id", params.id);
    const url = `${baseApi}${employeeLeavesBalances}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  updateEmployeeLeaveBalance: (id, payload) => {
    const { baseApi, employeeLeavesBalance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${employeeLeavesBalance}/${id}`;
    const params = {
      isApplicable: payload.isApplicable !== undefined ? payload.isApplicable : null,
      leave_type: payload.leave_type !== undefined ? (payload.leave_type || null) : null,
      yearlyCap: payload.yearlyCap !== undefined ? payload.yearlyCap : null,
      start: payload.start || null,
      end: payload.end || null,
      balance: payload.balance !== undefined ? payload.balance : null,
      isBalanceUsed: payload.isBalanceUsed !== undefined ? payload.isBalanceUsed : null,
      alreadyUsedBalance: payload.alreadyUsedBalance !== undefined ? payload.alreadyUsedBalance : null,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  deleteEmployeeLeaveBalance: (id) => {
    const { baseApi, employeeLeavesBalance } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${employeeLeavesBalance}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, {}, headers);
  },

  commissonDelete: (id) => {
    const { baseApi, deleteCommission } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteCommission + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  TravelAllowancesDelete: (id) => {
    const { baseApi, deleteTravelAllowances } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteTravelAllowances + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  CommissonUpdate: (
    commissionName,
    bonus,
    fromDate,
    toDate,
    fixed,
    description,
    id
  ) => {
    const { baseApi, udateCommission } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + udateCommission + id;
    const params = {
      start: fromDate,
      end: toDate,
      rate: bonus,
      rateType: fixed,
      name: commissionName,
      desc: description,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  TravelAllowancesUpdate: (id, payload) => {
    const { baseApi, updateTravelAllowances } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateTravelAllowances + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },

  handleCommissionRequest: (id, status) => {
    const { baseApi, handleCommissionRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCommissionRequest + id;
    const params = {
      status: status,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleTravelAllowancesRequest: (id, status) => {
    const { baseApi, handleTravelAllowancesRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleTravelAllowancesRequest + id;
    const params = {
      status: status,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  handleAddPayroll: (
    payrollId,
    fromDate,
    toDate,
    frequency,
    employees,
    paymentMethod,
    department
  ) => {
    const { baseApi, addPayroll } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addPayroll;
    const params = {
      payrollId: payrollId,
      employees: employees,
      paymentMethod: paymentMethod,
      frequency: frequency,
      start: fromDate,
      department: department,
      end: toDate,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  getLeviesTypes: (companyToken) => {
    const { baseApi, leviesTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + leviesTypes;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${companyToken}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getContribution: () => {
    const { baseUrl, contribution } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseUrl + contribution;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  contributionUpdate: (name, tax, id) => {
    const { baseUrl, updateContribution } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${updateContribution}/?id=${id}`;
    const params = {
      name: name,
      isTaxable: tax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  addLeaveRate: (rate, id) => {
    const { baseApi, addLeaveRate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${addLeaveRate}/?id=${id}`;
    const params = {
      leaveBalanceRate: rate,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  addTaxLabel: (title, country) => {
    const { baseApi, addTaxLabel } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${addTaxLabel}`;
    const params = {
      title: title,
      country: country,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  getCashFlow: (month, year, viewType) => {
    const { baseApi, getCashFlow } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getCashFlow}?month=${month}&year=${year}&viewType=${viewType}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getProfitLoss: (startDate, endDate) => {
    const { baseApi, getProfitLoss } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const start = startDate.format("YYYY-MM-DD");
    const end = endDate.format("YYYY-MM-DD");
    const url = `${baseApi}${getProfitLoss}?startDate=${start}&endDate=${end}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getTaxRule: (country, taxName) => {
    const { baseApi, getTaxRule } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getTaxRule}?country=${country}&taxName=${taxName}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  addTaxRule: (payload) => {
    const { baseApi, addTaxRule } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${addTaxRule}`;
    const params = serializeTaxRulePayload(payload);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  deleteTaxRule: (id) => {
    const { baseApi, deleteTaxRule } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteTaxRule + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteTaxRules: (country, taxName) => {
    const { baseApi, deleteTaxRules } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${deleteTaxRules}?country=${country}&taxName=${taxName}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  deleteTaxLabel: (id) => {
    const { baseApi, deleteTaxLabel } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${deleteTaxLabel}/${id}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  updateTaxRule: (id, payload) => {
    const { baseApi, updateTaxRUle } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${updateTaxRUle}?id=${id}`;
    const params = serializeTaxRulePayload(payload);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  createContribution: (name, tax) => {
    const { baseUrl, updateContribution } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseUrl}${updateContribution}`;
    const params = {
      name: name,
      isTaxable: tax,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  deleteContribution: (id) => {
    const { baseUrl, deleteContribution } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + deleteContribution + "/" + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getContributionEdit: (id, pId) => {
    const { baseApi, getcontribution } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getcontribution + id + "/" + pId;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  handleGetAllQuoteData: (text, sort, page) => {
    const { baseApi, getAllQuotesData } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();

    if (sort) params.append("sort", sort);
    if (text) params.append("search", text);
    if (page) params.append("page", page);

    const url = `${baseApi}${getAllQuotesData}${params.toString() ? `/?${params.toString()}` : ""
      }`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  handleDeleteQuote: (quotedId) => {
    const { baseApi, deleteQuote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};

    const url = `${baseApi}${deleteQuote}/${quotedId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallDelete(url, params, headers);
  },

  getQuoteView: (id) => {
    const { baseApi, getQuoteView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getQuoteView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  handleDownloadQuote: (id) => {
    const { baseApi, downloadQuote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + downloadQuote + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  sentQuote: (id) => {
    const { baseApi, sendQuote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + sendQuote + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  createQuote: async (quoteData) => {
    const { baseApi, quote } = ApiConfig;
    const url = baseApi + quote;
    const token = localStorage.getItem("accessToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, quoteData, headers);
  },

  getQuote: async (quoteId) => {
    const { baseApi, quote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${quote}/${quoteId}`;
    const params = {};

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, params, headers);
  },

  updateQuote: async (quoteId, quoteData) => {
    const { baseApi, quote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${quote}/${quoteId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, quoteData, headers);
  },
  updateInvoicePayment: async ({ invoiceId, transactionId, payload }) => {
    const { baseApi, handleInvoice } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleInvoice}/${invoiceId}/payment/${transactionId}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  getNotesReceiptView: (id) => {
    const { baseApi, getNotesReceiptView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getNotesReceiptView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleDownloadNotesReceipt: (id) => {
    const { baseApi, downloadNotesReceipt } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + downloadNotesReceipt + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  createNote: async (noteData) => {
    const { baseApi, noteCreate } = ApiConfig;
    const url = baseApi + noteCreate;
    const token = localStorage.getItem("accessToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, noteData, headers);
  },
  getEditNotesData: (id) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + noteCreate + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  updateNotes: async ({ id, payload }) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${noteCreate}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  getInvoiceList: (id) => {
    const { baseApi, invoiceList } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + invoiceList + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  updateCreditNotes: async ({ creditNoteId, payload }) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${noteCreate}/${creditNoteId}/applyCredit`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },
  createRefund: async (id, payload) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${noteCreate}/${id}/refund`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },
  sendNotes: (id) => {
    const { baseApi, sendNote } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + sendNote + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  deleteApplyCredit: (id, creditAppliedID) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${noteCreate}/${id}/applyCredit/${creditAppliedID}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteRefundHistory: (id, transactionId) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${noteCreate}/${id}/refund/${transactionId}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  updateRefundHistory: (creditNoteId, transactionId, payload) => {
    const { baseApi, noteCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${noteCreate}/${creditNoteId}/refund/${transactionId}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  getAllSalesOrderList: () => {
    const { baseApi, salesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${salesOrder}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },
  getSalesOrderView: (id) => {
    const { baseApi, salesOrderView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + salesOrderView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleDownloadSalesOrderReceipt: (id) => {
    const { baseApi, salesOrderDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + salesOrderDownload + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  sendSalesOrder: (id) => {
    const { baseApi, salesOrderSend } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + salesOrderSend + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleDeleteSalesOrder: (creditNoteId) => {
    const { baseApi, deleteSalesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${deleteSalesOrder}/${creditNoteId}`;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    return ApiCallDelete(url, params, headers);
  },

  createSalesOrder: async (salesData) => {
    const { baseApi, createSalesOrder } = ApiConfig;
    const url = baseApi + createSalesOrder;
    const token = localStorage.getItem("accessToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, salesData, headers);
  },
  fulfillmentComplete: (id) => {
    const { baseApi, createSalesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSalesOrder}/${id}/fulfill/complete`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  getSalesOrder: (id) => {
    const { baseApi, createSalesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSalesOrder}/${id}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  updatePartialFullfillment: (id, payload) => {
    const { baseApi, createSalesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSalesOrder}/${id}/fulfill/partial`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  updateSales: (id, payload) => {
    const { baseApi, createSalesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSalesOrder}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  handleAddNewInvoice: (id, payload) => {
    const { baseApi, createSalesOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createSalesOrder}/${id}/convertToInvoice`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },
  handleGetAllPurchaseOrder: () => {
    const { baseApi, allPurchaseOrders } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${allPurchaseOrders}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  createPurchaseOrder: (payload) => {
    const { baseApi, createPurchaseOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createPurchaseOrder}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, payload, headers);
  },
  updatePurchaseOrder: (id, payload) => {
    const { baseApi, createPurchaseOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${createPurchaseOrder}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },
  getOnePurchaseOrder: (id) => {
    const { baseApi, createPurchaseOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + createPurchaseOrder + "/" + id;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getPurchaseOrderView: (id) => {
    const { baseApi, getPurchaseOrderView } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams();
    const url = baseApi + getPurchaseOrderView + "/" + id;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleDownloadPurchaseOrderInvoice: (id) => {
    const { baseApi, handlePurchaseOrderDownload } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handlePurchaseOrderDownload + "/" + id;

    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },
  sentPurchaseOrder: (id) => {
    const { baseApi, sendPurchaseOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + sendPurchaseOrder + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  handleDeletePurchaseOrder: (id) => {
    const { baseApi, handleDeletePurchaseOrder } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${handleDeletePurchaseOrder}/${id}`;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    return ApiCallDelete(url, params, headers);
  },
  markReceive: (id) => {
    const { baseApi, markReceive } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url =
      baseApi + markReceive + "/" + id + "/receiveStatus?action=received";
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  handleGetSupplier: (id) => {
    const { baseApi, handleGetSupplier } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleGetSupplier}/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },
  markItemActive: (id, mark) => {
    const { baseApi, deleteItem } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    let url;
    if (mark) {
      url = `${baseApi}${deleteItem}/${id}/markActive`;
    } else {
      url = `${baseApi}${deleteItem}/${id}/markInactive`;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, {}, headers);
  },
  getEmployee: (id) => {
    const { baseApi, getEmployeeDetails } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = baseApi + getEmployeeDetails + id;

    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleGetAllAssets: () => {
    const { baseApi, handleAssest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${handleAssest}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },
  handleCreateNewAsset: async (assetData) => {
    const { baseApi, handleCreate } = ApiConfig;
    const url = baseApi + handleCreate;
    const token = localStorage.getItem("accessToken");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, assetData, headers);
  },
  handleGetOneData: (id) => {
    const { baseApi, handleCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${handleCreate}/${id}`;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    return ApiCallGet(url, params, headers);
  },

  handleDeleteAsset: (id) => {
    const { baseApi, handleCreate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${handleCreate}/${id}`;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    return ApiCallDelete(url, params, headers);
  },
  handleTaxTableImport: (data) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();

    formData.append("tax_data", data);

    const { baseApi, taxTableImport } = ApiConfig;
    const url = baseApi + taxTableImport;
    const headers = {
      // "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, formData, headers);
  },
  deleteCredit: (creditTypeId) => {
    const { baseApi, deleteCredit } = ApiConfig;

    const token = localStorage.getItem("accessToken");
    const url = baseApi + deleteCredit + "/" + creditTypeId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  deleteCreditById: (creditId) => {
    const { baseApi, addEmployeeCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + addEmployeeCredits + "/" + creditId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  createTravelAllowance: async (travelAllowanceRate, nonTaxableRate, start, end, country) => {
    const { baseApi, createTravelAllowance } = ApiConfig;

    const url = baseApi + createTravelAllowance;
    const token = localStorage.getItem("accessToken");

    const params = {
      travelAllowanceRate: travelAllowanceRate,
      nonTaxableRate: nonTaxableRate,
      start: start,
      end: end,
      country: country,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  getTravelsAllowances: (start, end, country) => {
    const { baseApi, createTravelAllowance } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    let url = baseApi + createTravelAllowance;
    const params = {};

    // Build query parameters
    const queryParams = [];
    if (start && end) {
      queryParams.push(`start=${encodeURIComponent(start)}`);
      queryParams.push(`end=${encodeURIComponent(end)}`);
    }
    if (country) {
      queryParams.push(`country=${encodeURIComponent(country)}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  editTravelAllowance: async (id, travelAllowanceRate, nonTaxableRate, start, end, country) => {
    const { baseApi, createTravelAllowance } = ApiConfig;

    const url = baseApi + createTravelAllowance + `/${id}`;
    const token = localStorage.getItem("accessToken");

    const params = {
      travelAllowanceRate: travelAllowanceRate,
      nonTaxableRate: nonTaxableRate,
      start: start,
      end: end,
      country: country,
    };

    // Add start and end dates if provided
    if (start) {
      params.start = start;
    }
    if (end) {
      params.end = end;
    }
    // Add country if provided
    if (country) {
      params.country = country;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  fetchCredits: async (employeeId) => {
    const { baseApi, getAllCredit } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getAllCredit}?employee=${employeeId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  // POST /credit_beneficiary
  handleCreateCredits: async ({ employee, credit_beneficiary }) => {
    const { baseApi, createCredits } = ApiConfig;

    const url = baseApi + createCredits;
    const token = localStorage.getItem("accessToken");

    const params = {
      employee: employee,
      credit_beneficiary: credit_beneficiary,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  // PUT /credit_beneficiaries
  handleUpdateCredits: async ({ employee, credit_beneficiary }) => {
    const { baseApi, updateCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${updateCredits}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },

  // DELETE /credit_beneficiary/:id
  handleDeleteCredits: async (beneficiaryId) => {
    const { baseApi, deleteCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const params = {};
    const url = `${baseApi}${deleteCredits}/${beneficiaryId}`;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    return ApiCallDelete(url, params, headers);
  },
  addNewEmployeeCredits: async (data) => {
    const { baseApi, createBenificiary } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + createBenificiary;
    const params = {
      ...data
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  updateBeneficiaryCredits: async (data) => {
    const { baseApi, updateCredits } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + updateCredits;
    const params = {
      ...data
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  fetchBenificiaries: async (employeeId, creditTypeId) => {
    const { baseApi, getBeneficieries } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getBeneficieries}?employee=${employeeId}&creditType=${creditTypeId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },
  handleDeletBeneficiary: async (beneficiaryId) => {
    const { baseApi, createBenificiary } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + createBenificiary + "/" + beneficiaryId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  handleCreditApply: async (isApplicable, id) => {
    const { baseApi, handleCreditApply } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCreditApply + "/" + id;
    const params = {
      isApplicable: isApplicable
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, params, headers);
  },
  postCreditApply: async (data) => {
    const { baseApi, handleCreditApply } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleCreditApply;
    const params = {
      ...data
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  getSingleCreditTypeId: async (id) => {
    const { baseApi, getSingleData } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getSingleData + '?employee=' + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getEmployeeTaxRulesAvailable: async (employeeId) => {
    const { baseApi, getEmployeeTaxRulesAvailable } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${getEmployeeTaxRulesAvailable}/${employeeId}/available`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleEmployeeTaxRules: async (data) => {
    const { baseApi, handleEmployeeTaxRules } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleEmployeeTaxRules;
    const params = {
      ...data
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },
  getLeavePdf: async (id) => {
    const { baseApi, leavePdf } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + leavePdf + '/' + id;
    const params = {};
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },
  getReportMenu: async () => {
    const { baseApi, reportMenu } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + reportMenu;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleWorkerDataExport: (id) => {
    const token = localStorage.getItem("accessToken");
    const params = {}
    const { baseApi, handleWorkerDataExport } = ApiConfig;
    const url = baseApi + handleWorkerDataExport + '/' + id;
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallGet(url, params, headers, "blob");
  },
  handleWorkerDataExportEmail: async (id, email) => {
    const token = localStorage.getItem("accessToken");
    const params = {};
    const { baseApi, handleWorkerDataExport } = ApiConfig;
    const url = `${baseApi}${handleWorkerDataExport}/${id}?type=email&email=${encodeURIComponent(email)}`;
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
    return ApiCallGet(url, params, headers);
  },
  getSingleTravelClaimData: async (id) => {
    const { baseApi, travelClaimRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + travelClaimRequest + '/' + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getTravelAllowanceDefaults: async (queryParams = {}) => {
    const { baseApi, travelClaimRequest } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const searchParams = Object.entries(queryParams)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");
    const url = baseApi + travelClaimRequest + (searchParams ? `?${searchParams}` : "");
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  getRebates: async ({ page = 1, limit = 10, country, start, end }) => {
    const { baseUrl, rebate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const queryParams = [];
    if (country) {
      queryParams.push(`country=${encodeURIComponent(country)}`);
    }
    if (start) {
      queryParams.push(`start=${encodeURIComponent(start)}`);
    }
    if (end) {
      queryParams.push(`end=${encodeURIComponent(end)}`);
    }
    if (page) {
      queryParams.push(`page=${page}`);
    }
    if (limit) {
      queryParams.push(`limit=${limit}`);
    }
    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
    const url = baseUrl + rebate + queryString;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  createRebate: async (data) => {
    const { baseUrl, rebate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebate;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, data, headers);
  },

  updateRebate: async (rebateId, data) => {
    const { baseUrl, rebate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebate + "/" + rebateId;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, data, headers);
  },

  deleteRebate: async (rebateId) => {
    const { baseUrl, rebate } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebate + "/" + rebateId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  getRebateThresholds: async ({ country, start, end, page = 1, limit = 20 } = {}) => {
    const { baseUrl, rebateThreshold } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const queryParams = [];
    if (country) {
      queryParams.push(`country=${encodeURIComponent(country)}`);
    }
    if (start) {
      queryParams.push(`start=${encodeURIComponent(start)}`);
    }
    if (end) {
      queryParams.push(`end=${encodeURIComponent(end)}`);
    }
    if (page) {
      queryParams.push(`page=${page}`);
    }
    if (limit) {
      queryParams.push(`limit=${limit}`);
    }
    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
    const url = baseUrl + rebateThreshold + queryString;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  getRebateThresholdById: async (rebateThresholdId) => {
    const { baseUrl, rebateThreshold } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebateThreshold + "/" + rebateThresholdId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  createRebateThreshold: async (data) => {
    const { baseUrl, rebateThreshold } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebateThreshold;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, data, headers);
  },

  updateRebateThreshold: async (rebateThresholdId, data) => {
    const { baseUrl, rebateThreshold } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebateThreshold + "/" + rebateThresholdId;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, data, headers);
  },

  deleteRebateThreshold: async (rebateThresholdId) => {
    const { baseUrl, rebateThreshold } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseUrl + rebateThreshold + "/" + rebateThresholdId;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  exportEmployeeSectionReport: (employeeId, section) => {
    const { baseApi, employeeSectionReport } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + employeeSectionReport + employeeId;
    const params = {
      section: section,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accept: "application/pdf",
    };
    return ApiCallPost(url, params, headers, "blob");
  },

  // Financial Year APIs (using tax-period endpoints)
  getFinancialYears: async (country) => {
    const { baseApi, taxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const queryParams = new URLSearchParams({ showAll: "true" });
    const normalizedCountry = String(country || "").trim();
    if (normalizedCountry) {
      queryParams.append("country", normalizedCountry);
    }
    const url = `${baseApi}${taxType}?${queryParams.toString()}`;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },

  addFinancialYear: async (data) => {
    const { baseApi, handleTaxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleTaxType;
    const startDate = data?.start || "";
    const endDate = data?.end || "";
    const country = String(data?.country || "").trim();
    const params = {
      start: startDate,
      end: endDate,
      isActive: data.isActive,
      country,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  updateFinancialYear: async (id, data) => {
    const { baseApi, handleTaxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleTaxType + "?id=" + id;
    const startDate = data?.start || "";
    const endDate = data?.end || "";
    const country = String(data?.country || "").trim();
    const params = {
      start: startDate,
      end: endDate,
      isActive: data.isActive,
      country,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  deleteFinancialYear: async (id) => {
    const { baseApi, handleTaxType } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + handleTaxType + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },
  getAllLeaveTypeForAdmin: async (taxCountry) => {
    const { baseApi, getAdminLeaveTypes } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + getAdminLeaveTypes + "?country=" + taxCountry;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, params, headers);
  },
  handleLeaveType: async (type, taxCountry) => {
    const { baseApi, leaveTypeForAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + leaveTypeForAdmin;
    const params = {
      type: type,
      country: taxCountry
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPost(url, params, headers);
  },

  deleteAdminLeaveType: async (id) => {
    const { baseApi, leaveTypeForAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + leaveTypeForAdmin + "/" + id;
    const params = {};
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallDelete(url, params, headers);
  },

  updateAdminLeaveType: async (id, type, taxCountry) => {
    const { baseApi, leaveTypeForAdmin } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi + leaveTypeForAdmin}/?id=${id}`;
    const body = {
      type,
      country: taxCountry,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, body, headers);
  },

  getPendingUpdates: async () => {
    const { baseApi, pendingUpdates } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = baseApi + pendingUpdates;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  handlePendingUpdateStatus: async (id, action, payload) => {
    const { baseApi, pendingUpdates } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${pendingUpdates}/${id}/${action}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallPut(url, payload, headers);
  },

  getUpdateHistory: async (employeeId) => {
    const { baseApi, pendingUpdatesHistory } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}${pendingUpdatesHistory}/${employeeId}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    return ApiCallGet(url, {}, headers);
  },

  // Forgot Password - Request OTP
  forgotPassword: async (data) => {
    const { baseApi } = ApiConfig;
    const url = baseApi + "forgot-password";
    const params = {
      email: data?.email?.value || data?.email,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    return ApiCallPost(url, params, headers);
  },

  // Verify OTP - Sent to user's email
  verifyOtp: async (email, otp) => {
    const { baseApi } = ApiConfig;
    const url = baseApi + "verify-otp";
    const params = {
      email: email,
      otp: otp,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    return ApiCallPost(url, params, headers);
  },

  resetPassword: async (data, token) => {
    const { baseApi } = ApiConfig;
    const url = baseApi + "reset-password";
    const params = {
      token: token,
      password: data?.newPassword?.value || data?.newPassword,
    };

    const headers = {
      "Content-Type": "application/json",
    };

    return ApiCallPost(url, params, headers);
  },

  // Sub Admin Management APIs
  getSubAdminList: async (page = 1, limit = 10, search = "") => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (limit) params.append("limit", limit);
    if (search) params.append("search", search);

    const url = `${baseApi}superadmin/subadmins${params.toString() ? `?${params.toString()}` : ""}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  getSubAdminById: async (id) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}superadmin/subadmin/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallGet(url, {}, headers);
  },

  createSubAdmin: async (data) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}superadmin/subadmin`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPost(url, data, headers);
  },

  updateSubAdmin: async (id, data) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}superadmin/subadmin/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, data, headers);
  },

  deleteSubAdmin: async (id) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");
    const url = `${baseApi}superadmin/subadmin/${id}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallDelete(url, {}, headers);
  },
  // GET Marketing Communication
  getMarketingCommunication: async (id) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}marketing-communication/${id}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    // Important: Pass null as second parameter (params)
    return ApiCallGet(url, null, headers);
  },

  // PUT Update Marketing Communication
  updateMarketingCommunication: async (id, data) => {
    const { baseApi } = ApiConfig;
    const token = localStorage.getItem("accessToken");

    const url = `${baseApi}marketing-communication/${id}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return ApiCallPut(url, data, headers);
  },
};

export default AuthService;
